import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { usePersistedState } from '../hooks/usePersistedState';
import {
  Box, Typography, Card, CardContent, CardHeader, Tabs, Tab,
  Table, TableBody, TableCell, TableHead, TableRow, TablePagination,
  Chip, Button, Switch, FormControlLabel, TextField,
  Select, MenuItem, FormControl, InputLabel, CircularProgress,
  Grid, Tooltip, Snackbar, Alert, LinearProgress,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
// rockBandService + its in-house-polling types removed — the legacy in-house
// RockBand/DailyBlast polling (tracked-underlyings) is retired; signals now come
// only from the scheduled-scan engine. Tracked-underlyings UI removed below.
import { strategyScanService, StrategyWatchlist, StrategySignalItem, StrategyScanJob } from '../services/strategyScan.service';
import { useAppTheme } from '../hooks/useAppTheme';
import { AppColorTokens } from '../styles/colorTokens';
import api from '../services/api';
import toast from 'react-hot-toast';
import ScanProgress from '../components/strategies/ScanProgress';
import AiVerdictChip from '../components/ai/AiVerdictChip';
import ScheduledScanPanel from '../components/strategies/ScheduledScanPanel';
import EmptyState from '../components/common/EmptyState';
import TimeframeMappingPanel from '../components/strategies/TimeframeMappingPanel';
import StrategyComparisonStrip from '../components/strategies/StrategyComparisonStrip';
import { StrategyOverview, type StrategyOverviewSpec } from '../components/strategies/StrategyOverview';
import {
  customIntervalService, FALLBACK_VOCABULARY,
  type IntervalVocabulary,
} from '../services/customInterval.service';
import { OVERVIEW_ZONE_COLORS } from '../theme/overviewColors';
import { useTabs } from '../contexts/TabsContext';
import { BUILTIN_INTERVALS, intervalLabel } from '../utils/intervalCatalog';

/** Card ids, row engines and row names all spell the same strategy differently
 *  ('om_simple' / 'OM_Simple' / 'shaved_doji' / 'Shaved Doji'), so anything that
 *  matches one against another goes through here. */
const normStrategyKey = (n?: string) => (n || '').toLowerCase().replace(/[\s_-]/g, '');

// R76 — Sniper moved to top of the list + made default. Description updated to
// reflect the new CMid + cloud-colour-flip logic.
// F-Strategies-TileOrder — RockBand first so it's the default landing strategy
// (matches the default selectedStrategy).
const STRATEGIES = [
  {
    id: 'rockband' as const,
    name: 'RockBand',
    description: 'OverView-trend strategy — aggressive entries when the HTF is aligned and the chart-TF score flips through threshold; score-based exits',
    status: 'active' as 'active' | 'coming_soon',
  },
  {
    id: 'sniper' as const,
    name: 'Sniper',
    description: 'AvgCloud (LR_HAMA ↔ CMid) — BUY / SHORT fire on cloud-colour flip, confirmed by HTF cloud direction',
    status: 'active' as const,
  },
  {
    id: 'dailyblast' as const,
    name: 'DailyBlast',
    description: 'LinReg-smoothed Heikin-Ashi with 4-state candle model and 2-bar breakout entries',
    status: 'active' as const,
  },
  {
    id: 'shaved_doji' as const,
    name: 'Shaved Doji',
    description: 'CLH-close stop-and-reverse — BUY when the CLH close rises above the prior CLH close, SHORT when it falls below (flips each time). CLH closes only, any candle type.',
    status: 'active' as const,
  },
  {
    id: 'blast' as const,
    name: 'Blast',
    description: 'Inside (HA) / Engulfing (Regular) detection on Daily & Weekly draws a High/Low band for 2 periods; BUY/SHORT on a fresh C-POC breakout while the band is active.',
    status: 'active' as const,
  },
  {
    id: 'om_simple' as const,
    name: 'OM_Simple',
    description: 'CLN colour flip — BUY when the candle colour turns red → green, SHORT when it turns green → red. Entries only: no Sell/Cover, no stop of its own, auto-trade refused. Runs on any interval including custom ones (7H).',
    status: 'active' as const,
  },
  {
    id: 'lr_cross' as const,
    name: 'LR_Cross',
    description: 'Alerts when a closed candle (Regular or Heikin-Ashi) crosses the LR body: BUY on the first close above the body top, SHORT on the first close below the bottom; a cross the other way also exits (BUY ✕S / SHORT ✕L). LR length follows your chart. Alerts only, auto-trade refused.',
    status: 'active' as const,
  },
  {
    id: 'bhairav' as const,
    name: 'Bhairav',
    description: 'Body-high/body-low stop-and-reverse on HA/CLH candles with backtest-curated trend gates — 1D: AvgCloud+EMA50 · 4H: Daily+Weekly alignment · 1W: raw. Runs on 4H/1D/1W only; Charts chip shows the exact gated signals.',
    status: 'active' as const,
  },
];

interface StrategyLite {
  id: number;
  name: string;
  description?: string;
  status?: string;
  /** Scan engine: 'rockband' | 'dailyblast' | 'sniper'. */
  engine?: string;
  /** jsonb config payload (RockBand thresholds live under .rockband). */
  configuration?: any;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function actionColor(action: string, C: AppColorTokens): string {
  switch (action) {
    case 'BUY':        return C.bull;
    case 'SHORT':      return C.bear;
    case 'SELL_FLAT':  return C.warn;
    case 'COVER_FLAT': return C.neutral;
    default:           return C.dim;
  }
}

function positionInfo(pos: number, C: AppColorTokens): { label: string; color: string; bg: string } {
  switch (pos) {
    case 1:  return { label: 'LONG',  color: C.bull,    bg: C.bullMuted };
    case -1: return { label: 'SHORT', color: C.bear,    bg: C.bearMuted };
    default: return { label: 'FLAT',  color: C.dim,     bg: C.dimMuted };
  }
}

/** Bhairav's curated production ladder — scans/autotrade run on 4H/1D/1W ONLY
 *  (sub-4H entries lose to costs in every backtested configuration). */
const BHAIRAV_TF_OPTIONS = [240, 1440, 10080];
const EXCHANGE_OPTIONS = ['DELTA', 'NSE', 'NFO', 'BSE', 'MCX', 'CDS', 'BFO'];

function formatTs(ts: string | null | undefined) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false });
}

// The 43200 -> '1M' case here used to be a label with no data pipeline behind
// it: nothing in the app could produce a monthly bar. It can now.
const minutesLabel = intervalLabel;


// ── TabPanel ─────────────────────────────────────────────────────────────────

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const StrategiesPage: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const { C } = useAppTheme();
  const { openChartTab } = useTabs();
  // Persist the inputs the user spent time setting up so navigating away and
  // back doesn't reset their tab / strategy / watchlist / interval / date.
  const [tab, setTab] = usePersistedState<number>('strategies.tab', 0);
  // Watchlists tab (value 3) was removed — migrate any persisted selection.
  useEffect(() => { if (tab === 3) setTab(0); }, [tab, setTab]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success'
  });

  // (Tracked-underlyings / in-house-polling state removed — engine retired.)

  // Strategy selector
  // R76 — Sniper is now the first + default strategy.
  const [selectedStrategy, setSelectedStrategy] = usePersistedState<'dailyblast' | 'rockband' | 'shaved_doji' | 'sniper' | 'blast' | 'bhairav' | 'om_simple' | 'lr_cross'>('strategies.selectedStrategy', 'rockband');

  // Expanded signal row
  const [expandedSignalId, setExpandedSignalId] = useState<number | null>(null);

  // ── Strategy Scanning state ──
  const [scanAsOfDate, setScanAsOfDate] = usePersistedState<string>('strategies.scanAsOfDate', '');   // YYYY-MM-DD ; empty = live (today)
  const [watchlists, setWatchlists] = useState<StrategyWatchlist[]>([]);
  const [scanSignals, setScanSignals] = useState<StrategySignalItem[]>([]);
  // Signal Log column sorting — tri-state per column: asc → desc → off (back to
  // the server's original order). `sortCol` null = unsorted.
  const [signalSortCol, setSignalSortCol] = usePersistedState<string | null>('strategies.signalSortCol', null);
  const [signalSortDir, setSignalSortDir] = usePersistedState<'asc' | 'desc' | null>('strategies.signalSortDir', null);
  const [scanSignalTotal, setScanSignalTotal] = useState(0);
  const [scanSignalPage, setScanSignalPage] = useState(0);
  const [signalDayFilter, setSignalDayFilter] = usePersistedState<number>('strategies.signalDayFilter', 1);
  // F-Strategies-Filter — when set, overrides signalDayFilter and shows
  // ONLY signals whose candleTime falls inside this single IST day.
  const [signalCandleDate, setSignalCandleDate] = usePersistedState<string>('strategies.signalCandleDate', '');
  const [scanSignalFilterSymbol, setScanSignalFilterSymbol] = usePersistedState<string>('strategies.scanSignalFilterSymbol', '');
  // F-Strategies-ActionChips — multi-select. Empty array = "all actions".
  // IMPORTANT: NOT persisted across sessions — a previously-picked filter
  // ghosting back when the user re-visits the page caused real confusion
  // (an old SHORT pick survived a reload, the user clicked BUY thinking
  // they were starting fresh, and the request went out as SHORT+BUY).
  // In-session navigation (Strategies → Charts → back) preserves selection
  // because the component remounts but is re-mounted from the same React
  // tree; only a hard reload / new tab clears it.
  const [scanSignalFilterActions, setScanSignalFilterActions] = usePersistedState<string[]>('strategies.scanSignalFilterActions', []);
  const [scanSignalFilterInterval, setScanSignalFilterInterval] = usePersistedState<number | ''>('strategies.scanSignalFilterInterval', '');
  // F-Strategies-RefreshPerf — UI lock during the manual refresh round-trip.
  const [refreshingPerf, setRefreshingPerf] = useState(false);
  const [scanRunning, setScanRunning] = useState(false);
  const [lastScanSummary, setLastScanSummary] = useState<{ total: number; counts: Record<string, number> } | null>(null);
  const [scanInterval, setScanInterval] = usePersistedState<number>('strategies.scanInterval', 15);
  const [selectedWatchlistId, setSelectedWatchlistId] = usePersistedState<number | null>('strategies.selectedWatchlistId', null);

  // Strategies owned by the user (sourced from /strategies) + the currently selected
  // one for Strategy Scan / Scheduled Scan. Backend auto-creates a default on first load.
  const [userStrategies, setUserStrategies] = useState<StrategyLite[]>([]);
  const [scanStrategyId, setScanStrategyId] = useState<number | null>(null);
  // The user's full interval vocabulary, so a CUSTOM interval defined on Charts
  // (7H) can actually be scanned and scheduled here. This page used to hardcode
  // a builtin-only list and never call /intervals at all, so a custom interval
  // was invisible here no matter what the user did on the Charts side.
  const [intervalVocab, setIntervalVocab] = useState<IntervalVocabulary>(FALLBACK_VOCABULARY);
  useEffect(() => {
    let alive = true;
    customIntervalService.list()
      .then(v => { if (alive) setIntervalVocab(v); })
      .catch(() => { /* keep the builtin fallback */ });
    return () => { alive = false; };
  }, []);
  const customByMinutes = useMemo(
    () => new Map(intervalVocab.custom.map(c => [c.minutes, c])),
    [intervalVocab],
  );
  const allIntervalOptions = useMemo(
    () => (intervalVocab.scanEligible.length ? intervalVocab.scanEligible : [...BUILTIN_INTERVALS]),
    [intervalVocab],
  );
  // A custom interval carries the user's own name for it ("7H"); a builtin uses
  // the catalog label. One labeller so the pickers below cannot disagree.
  const labelFor = useCallback(
    (m: number) => customByMinutes.get(m)?.label || minutesLabel(m),
    [customByMinutes],
  );

  // What the Signal Log's interval filter offers. The union with the intervals
  // actually PRESENT in the loaded signals matters: a signal you can see must be
  // filterable, and that includes one logged on an interval since deleted from
  // the picker — which the old builtin-only list could never show.
  const signalFilterIntervals = useMemo(() => {
    const s = new Set<number>(allIntervalOptions);
    for (const sig of scanSignals) if (sig.intervalMinutes) s.add(sig.intervalMinutes);
    return Array.from(s).sort((a, b) => a - b);
  }, [allIntervalOptions, scanSignals]);

  // Bhairav scans are restricted to its curated production ladder (4H/1D/1W);
  // other engines keep the full interval list.
  const scanEngine = userStrategies.find(s => s.id === scanStrategyId)?.engine || 'rockband';
  const scanIntervalOptions = scanEngine === 'bhairav' ? BHAIRAV_TF_OPTIONS : allIntervalOptions;
  useEffect(() => {
    if (scanEngine === 'bhairav' && !BHAIRAV_TF_OPTIONS.includes(scanInterval)) setScanInterval(240);
  }, [scanEngine, scanInterval, setScanInterval]);
  // R145 — "closed Higher-Timeframe candles only" toggle per strategy. Only the
  // HTF strategies (RockBand / Sniper) have look-ahead to disable; single-TF
  // strategies are always closed. Stored on Strategy.configuration.<engine>.
  const [closedHtfFlag, setClosedHtfFlag] = useState(false);
  const [closedHtfSaving, setClosedHtfSaving] = useState(false);

  // Symbol Manager — watchlist filter
  const [smFilterWatchlistId, setSmFilterWatchlistId] = useState<number | ''>('');

  // Live scan progress — id of the job to show under the adhoc scan controls.
  const [activeScanJobId, setActiveScanJobId] = useState<number | null>(null);

  // Tracks whether the currently-selected strategy has an enabled scheduled scan.
  // null = no schedule configured; true/false reflect ScheduledScanPanel state.
  const [scheduleEnabled, setScheduleEnabled] = useState<boolean | null>(null);

  // Per-strategy schedule state for rendering ACTIVE/PAUSED chips on every card
  // (not just the selected one). Keyed by UI string id ('dailyblast', 'rockband', ...).
  const [scheduleEnabledMap, setScheduleEnabledMap] = useState<Record<string, boolean | null>>({});

  const notify = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnack({ open: true, message, severity });
  };

  // (loadAll for tracked-underlyings/in-house polling removed — engine retired.)

  // ── Scanning data loaders ──
  const loadStrategies = useCallback(async () => {
    try {
      const res = await api.get<any>('/strategies');
      const items: StrategyLite[] = res.data?.strategies || [];
      setUserStrategies(items);
      // Default-pick the first strategy if nothing selected yet.
      setScanStrategyId(prev => prev ?? (items[0]?.id || null));
    } catch { /* ignore */ }
  }, []);

  const loadWatchlists = useCallback(async () => {
    try {
      // Strategies scans + schedules can also run on "★ My favourites" (starred symbols).
      const items = await strategyScanService.listWatchlists({ includeFavourites: true });
      setWatchlists(items || []);
    } catch { /* ignore */ }
  }, []);

  // F-Strategies-MultiSchedule — stable onChange for ScheduledScanPanel.
  // An inline `onChange={(...) => ...}` was a fresh closure on every
  // render, which made the panel's internal load() callback re-create,
  // which re-ran its useEffect, which called load() again, which fired
  // onChange — infinite loop that pinned the panel at "Loading schedules…"
  // and prevented the tile chip from ever stabilising.
  const handleScheduleChange = useCallback((_first: any, all: any[]) => {
    const anyEnabled = all.some(s => !!s.enabled);
    const next = all.length === 0 ? null : anyEnabled;
    setScheduleEnabled(next);
    setScheduleEnabledMap(prev => ({ ...prev, [selectedStrategy]: next }));
  }, [selectedStrategy]);

  const loadScanSignals = useCallback(async () => {
    if (!scanStrategyId) { setScanSignals([]); setScanSignalTotal(0); return; }
    const reqParams = {
      // candleDate (single-day) overrides the days chip when set.
      ...(signalCandleDate ? { candleDate: signalCandleDate } : { days: signalDayFilter }),
      symbol: scanSignalFilterSymbol || undefined,
      // F-Strategies-ActionChips — comma-separated multi-action filter.
      // Empty list = no filter (server returns all actions).
      actions: scanSignalFilterActions.length ? scanSignalFilterActions.join(',') : undefined,
      intervalMinutes: scanSignalFilterInterval || undefined,
      // F-Strategies-NoPagination — fetch the whole filtered list in one shot.
      limit: 2000,
    };
    try {
      const res = await strategyScanService.getSignals(scanStrategyId, reqParams);
      setScanSignals(res.data?.data || []);
      setScanSignalTotal(res.data?.total || 0);
    } catch { /* ignore */ }
  }, [scanStrategyId, signalDayFilter, signalCandleDate, scanSignalFilterSymbol, scanSignalFilterActions, scanSignalFilterInterval]);

  // F-Strategies-RefreshPerf — recompute LTP/MaxGain/MaxAdv for the
  // currently-visible signals via a single round-trip (no per-row polling).
  const refreshSignalsPerf = useCallback(async () => {
    if (!scanStrategyId || scanSignals.length === 0 || refreshingPerf) return;
    const ids = scanSignals.map(s => s.id).filter(n => typeof n === 'number') as number[];
    if (ids.length === 0) return;
    setRefreshingPerf(true);
    try {
      const updated = await strategyScanService.refreshSignalsPerformance(scanStrategyId, ids);
      if (updated && updated.length) {
        const byId = new Map(updated.map(s => [s.id, s]));
        setScanSignals(prev => prev.map(s => byId.get(s.id) ? { ...s, ...byId.get(s.id) } : s));
        toast.success(`Refreshed ${updated.length} signal${updated.length === 1 ? '' : 's'}`);
      } else {
        toast.success('Refresh complete (no rows changed)');
      }
    } catch {
      toast.error('Failed to refresh performance');
    } finally {
      setRefreshingPerf(false);
    }
  }, [scanStrategyId, scanSignals, refreshingPerf]);

  useEffect(() => { loadStrategies(); }, [loadStrategies]);
  useEffect(() => { loadWatchlists(); }, [loadWatchlists]);

  // Lock the Strategy Scan / Scheduled Scan / Signal Log tabs onto the strategy
  // row that backs the selected card, so scans run the right engine and the
  // signal log reads that strategy's rows. All auto-created by the backend
  // bootstrap. Sniper → engine='sniper'; RockBand/DailyBlast → the row whose
  // (normalised) name matches the card.
  useEffect(() => {
    if (selectedStrategy === 'bhairav') {
      const row = userStrategies.find(s => s.engine === 'bhairav')
        || userStrategies.find(s => (s.name || '').toLowerCase() === 'bhairav');
      if (row) setScanStrategyId(row.id);
      return;
    }
    if (selectedStrategy === 'om_simple') {
      const row = userStrategies.find(s => s.engine === 'om_simple')
        // The character class was `[s_-]`, missing its backslash, so it stripped
        // every literal "s" too and 'om_simple' normalised to 'omimple' — this
        // name fallback could never match.
        || userStrategies.find(s => normStrategyKey(s.name) === 'omsimple');
      // If the row does not exist yet (the backend has not been updated), do NOT
      // leave the previous card's strategy id selected — "Run scan" would then
      // quietly scan with a DIFFERENT engine. Null it and let the guard below
      // explain instead.
      setScanStrategyId(row ? row.id : null);
      return;
    }
    if (selectedStrategy === 'lr_cross') {
      const row = userStrategies.find(s => s.engine === 'lr_cross')
        || userStrategies.find(s => normStrategyKey(s.name) === 'lrcross');
      // Same reason as OM_Simple: never leave another card's strategy selected.
      setScanStrategyId(row ? row.id : null);
      return;
    }
    if (selectedStrategy === 'sniper') {
      const sniperRow = userStrategies.find(s => s.engine === 'sniper');
      if (sniperRow) setScanStrategyId(sniperRow.id);
      return;
    }
    if (selectedStrategy === 'shaved_doji') {
      const row = userStrategies.find(s => s.engine === 'shaved_doji')
        || userStrategies.find(s => (s.name || '').toLowerCase().replace(/[\s_-]/g, '') === 'shaveddoji');
      if (row) setScanStrategyId(row.id);
      return;
    }
    if (selectedStrategy === 'blast') {
      const row = userStrategies.find(s => s.engine === 'blast')
        || userStrategies.find(s => (s.name || '').toLowerCase().replace(/[\s_-]/g, '') === 'blast');
      if (row) setScanStrategyId(row.id);
      return;
    }
    if (selectedStrategy === 'rockband' || selectedStrategy === 'dailyblast') {
      const norm = (n?: string) => (n || '').toLowerCase().replace(/[\s_-]/g, '');
      const row = userStrategies.find(s => norm(s.name) === selectedStrategy);
      if (row) setScanStrategyId(row.id);
    }
  }, [selectedStrategy, userStrategies]);

  // R145 — closed-HTF toggle: only RockBand/Sniper use a Higher Timeframe.
  const strategyEngineKey: 'rockband' | 'sniper' | null =
    selectedStrategy === 'rockband' ? 'rockband' : selectedStrategy === 'sniper' ? 'sniper' : null;
  // Load the current flag for the selected strategy (Sniper defaults ON — it's
  // already non-repainting; RockBand defaults OFF to preserve current behavior).
  useEffect(() => {
    if (!strategyEngineKey || !scanStrategyId) return;
    const row = userStrategies.find(s => s.id === scanStrategyId);
    const cfg = (row?.configuration as any)?.[strategyEngineKey] || {};
    const def = strategyEngineKey === 'sniper';
    setClosedHtfFlag(cfg.closedHtfOnly !== undefined ? !!cfg.closedHtfOnly : def);
  }, [strategyEngineKey, scanStrategyId, userStrategies]);

  const handleToggleClosedHtf = async (val: boolean) => {
    if (!strategyEngineKey || !scanStrategyId) return;
    setClosedHtfFlag(val);
    setClosedHtfSaving(true);
    try {
      const row = userStrategies.find(s => s.id === scanStrategyId);
      const existing = (row?.configuration as any) || {};
      const nextConfig = { ...existing, [strategyEngineKey]: { ...(existing[strategyEngineKey] || {}), closedHtfOnly: val } };
      await strategyScanService.updateStrategy(scanStrategyId, { configuration: nextConfig });
      setUserStrategies(prev => prev.map(s => s.id === scanStrategyId ? { ...s, configuration: nextConfig } : s));
      notify(`Saved — ${val ? 'closed HTF candles only' : 'using forming HTF bar'}. Applies on next scan / chart reload.`);
    } catch (e: any) {
      setClosedHtfFlag(!val);
      notify(e?.response?.data?.error?.message || 'Failed to save setting', 'error');
    } finally {
      setClosedHtfSaving(false);
    }
  };


  // LR_Cross source candle — stored on Strategy.configuration.lrCross.source and
  // read by lrCrossScan.service on each run.
  const lrCrossRow = userStrategies.find(s => s.engine === 'lr_cross');
  const lrCrossSource: 'Regular' | 'HA' = (lrCrossRow?.configuration as any)?.lrCross?.source === 'HA' ? 'HA' : 'Regular';
  const [lrCrossSaving, setLrCrossSaving] = useState(false);
  const handleLrCrossSource = async (val: 'Regular' | 'HA') => {
    if (!lrCrossRow) return;
    setLrCrossSaving(true);
    try {
      const existing = (lrCrossRow.configuration as any) || {};
      const nextConfig = { ...existing, lrCross: { ...(existing.lrCross || {}), source: val } };
      await strategyScanService.updateStrategy(lrCrossRow.id, { configuration: nextConfig });
      setUserStrategies(prev => prev.map(s => s.id === lrCrossRow.id ? { ...s, configuration: nextConfig } : s));
      notify(`Saved — LR_Cross scans now use ${val === 'HA' ? 'Heikin-Ashi' : 'Regular'} closes from the next run.`);
    } catch (e: any) {
      notify(e?.response?.data?.error?.message || 'Failed to save setting', 'error');
    } finally {
      setLrCrossSaving(false);
    }
  };

  // Fetch schedule state for every active strategy card so the ACTIVE/PAUSED
  // chip reflects reality for all cards, not only the selected one.
  useEffect(() => {
    if (!userStrategies.length) return;
    // Driven by the CARDS themselves, not a second hardcoded list. The old list
    // was ['sniper','dailyblast','rockband','blast'], so every engine added
    // after it (OM_Simple, Bhairav, Shaved Doji) was never fetched, fell through
    // to the selected-strategy value — null until the Strategy Scan tab is
    // opened — and read INACTIVE on the card while its scheduled scans were
    // running and posting alerts. Exactly the sniper bug the old comment here
    // described, repeated for the newer engines. Still bounded to the cards:
    // /strategies can hold rows that have no tile, and those need no chip.
    const cardKeys = new Set(STRATEGIES.map(c => normStrategyKey(c.id)));
    let cancelled = false;
    (async () => {
      const next: Record<string, boolean | null> = {};
      for (const row of userStrategies) {
        const key = normStrategyKey(row.engine || row.name);
        if (!key || !cardKeys.has(key)) continue;
        try {
          // F-Strategies-MultiSchedule — chip reflects "any enabled".
          const list = await strategyScanService.listSchedules(row.id);
          if (list.length === 0) next[key] = null;
          else next[key] = list.some(s => !!s.enabled);
        } catch {
          next[key] = null;
        }
      }
      if (!cancelled) setScheduleEnabledMap(next);
    })();
    return () => { cancelled = true; };
  }, [userStrategies]);
  useEffect(() => { loadScanSignals(); }, [loadScanSignals]);

  // F-Strategies-RefreshPerf — the 30s auto-poll was removed per user
  // bandwidth concern. New scheduled-scan signals + LTP/MaxGain/MaxAdv
  // updates surface via the explicit Refresh button on the SignalLog card.

  const handleStartScan = async () => {
    if (!scanStrategyId) { toast.error('Select a strategy'); return; }
    if (!selectedWatchlistId) { toast.error('Select a watchlist first'); return; }
    setScanRunning(true);
    setActiveScanJobId(null);
    try {
      const result = await strategyScanService.startScan(scanStrategyId, {
        watchlistId: selectedWatchlistId,
        intervalMinutes: scanInterval,
        ...(scanAsOfDate ? { asOfDate: scanAsOfDate } : {}),
      });
      // Backend returns the scan job row immediately (fire-and-forget). Use its id
      // to start polling live progress in the ScanProgress component.
      const jobId = result?.data?.id || result?.id;
      if (jobId) setActiveScanJobId(Number(jobId));
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'Scan failed to start');
      setScanRunning(false);
    }
  };

  const handleScanDone = useCallback((job: StrategyScanJob) => {
    setScanRunning(false);
    if (job.status === 'COMPLETED') {
      const perSym = job.perSymbolStatus || [];
      const counts = { BUY: 0, SHORT: 0, SELL: 0, COVER: 0 } as Record<string, number>;
      for (const e of perSym) {
        if (e.status === 'SIGNAL' && e.signalAction && counts[e.signalAction] !== undefined) {
          counts[e.signalAction]++;
        }
      }
      const total = job.signalsFound || 0;
      const breakdown = Object.entries(counts)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ');
      setLastScanSummary({ total, counts });
      toast.success(
        breakdown
          ? `Scan complete — ${total} signals (${breakdown})`
          : `Scan complete — 0 signals`
      );
    } else {
      toast.error(`Scan failed: ${job.error || 'unknown error'}`);
      setLastScanSummary(null);
    }
    loadScanSignals();
  }, [loadScanSignals]);

  // (Watchlist create/add/remove/delete handlers removed — watchlist management
  //  is centralized on the Watchlist page; this page only SELECTS a watchlist.)

  // (Tracked-underlyings handlers + warmup removed — in-house polling retired.)

  // ── Tab 0: Overview ──────────────────────────────────────────────────────────

  // ── Uniform Overview specs (R141) — one layout (StrategyOverview), per-
  //    strategy rules + candle colours. Conditions/colours are sourced from
  //    each strategy's engine so they stay accurate.
  const sniperOverviewSpec: StrategyOverviewSpec = {
    title: 'Sniper — Dual-Timeframe CLH Contrarian Pullback',
    summary: (<>Sniper trades on <strong>CLH (Custom Linreg-HA)</strong> candles. The mapped
      <strong> Higher Timeframe</strong> trend sets the allowed direction; a chart-timeframe
      candle pattern plus a <strong>volume spike</strong> then triggers the entry — buying dips
      inside an HTF uptrend (and shorting pops inside an HTF downtrend), or joining a fresh
      continuation. HTF comes from the Timeframe Mapping panel above and only CLOSED HTF candles
      are used (no look-ahead).</>),
    entry: [
      { tag: 'B_C1', tagColor: C.bull, label: 'Contrarian dip-buy', desc: 'HTF up, chart-TF down, no upper wick, and a volume spike — buy the pullback.' },
      { tag: 'B_C2', tagColor: C.bull, label: 'Rejection-wick buy', desc: 'HTF up, chart-TF down, a lower wick present, and a bullish volume spike.' },
      { tag: 'B_C3', tagColor: C.bull, label: 'Continuation buy', desc: 'HTF up, chart-TF up, no lower wick, and a bullish volume spike — join the trend.' },
      { tag: 'S_C1', tagColor: C.bear, label: 'Contrarian pop-short', desc: 'HTF down, chart-TF up, no lower wick, and a volume spike — short the bounce.' },
      { tag: 'S_C2', tagColor: C.bear, label: 'Rejection-wick short', desc: 'HTF down, chart-TF up, an upper wick present, and a bearish volume spike.' },
      { tag: 'S_C3', tagColor: C.bear, label: 'Continuation short', desc: 'HTF down, chart-TF down, no upper wick, and a bearish volume spike.' },
    ],
    exit: [
      { tag: 'SELL', tagColor: C.bear, label: 'Close long', desc: 'The first fresh chart-TF downtrend that begins a later cycle than the BUY closes the long.' },
      { tag: 'COVER', tagColor: C.bull, label: 'Close short', desc: 'The first fresh chart-TF uptrend that begins a later cycle than the SHORT closes the short.' },
    ],
    candleColors: [
      { color: C.bull, label: 'Green', when: 'Position state is LONG (in a Sniper long).' },
      { color: C.bear, label: 'Red', when: 'Position state is SHORT (in a Sniper short).' },
      { color: C.neutral, label: 'Neutral', when: 'FLAT — no open Sniper position.' },
    ],
    notes: (<>Volume spike = weighted volume &gt; slow average × 2; a bullish / bearish spike is a
      spike on an up / down raw candle. ISL = lowest low of the previous 3 CLH bars (BUY) or highest
      high (SHORT); Target = entry ± N × risk (N from the shared Target setting). One position per
      symbol — an opposite entry only fires when FLAT (no auto-flip).</>),
  };

  const rockbandOverviewSpec: StrategyOverviewSpec = {
    title: 'RockBand — OverView Trend Alignment',
    summary: (<>RockBand takes aggressive entries when the chart-timeframe trend and the
      <strong> Higher-Timeframe</strong> trend agree. The chart-TF candle is coloured by its
      <strong> OverView score</strong>; the background shows the always-on HTF OverView zone.
      Thresholds come from <strong>Chart Settings → OverView</strong> (Strong / Probable, default 60 / 20).</>),
    entry: [
      { tag: 'BUY', tagColor: C.bull, label: 'High', desc: 'Green chart-TF candle while the HTF zone is Strong Buy.' },
      { tag: 'BUY', tagColor: C.bull, label: 'Medium', desc: 'HTF zone Probable Buy and trending up.' },
      { tag: 'BUY', tagColor: C.bull, label: 'Low', desc: 'HTF zone Neutral and trending up.' },
      { tag: 'SHORT', tagColor: C.bear, label: 'High', desc: 'Red chart-TF candle while the HTF zone is Strong Short.' },
      { tag: 'SHORT', tagColor: C.bear, label: 'Medium', desc: 'HTF zone Probable Short and trending down.' },
      { tag: 'SHORT', tagColor: C.bear, label: 'Low', desc: 'HTF zone Neutral and trending down.' },
    ],
    exit: [
      { tag: 'SELL', tagColor: C.bear, label: 'Close long', desc: 'A red chart-TF candle closes an open long.' },
      { tag: 'COVER', tagColor: C.bull, label: 'Close short', desc: 'A green chart-TF candle closes an open short. A qualifying opposite entry flips the position.' },
    ],
    candleColors: [
      { color: C.bull, label: 'Green', when: 'Chart-TF OverView score ≥ +Strong (default +60).' },
      { color: C.bear, label: 'Red', when: 'Chart-TF OverView score ≤ −Strong (default −60).' },
      { color: C.neutral, label: 'Neutral', when: 'Score between the thresholds.' },
    ],
    backgroundColors: [
      { color: OVERVIEW_ZONE_COLORS.strongBuy, label: 'Strong Buy', when: 'HTF score ≥ +Strong.' },
      { color: OVERVIEW_ZONE_COLORS.probableBuy, label: 'Probable Buy', when: 'HTF score ≥ +Probable.' },
      { color: OVERVIEW_ZONE_COLORS.neutral, label: 'Neutral', when: 'HTF score between ±Probable.' },
      { color: OVERVIEW_ZONE_COLORS.probableShort, label: 'Probable Short', when: 'HTF score ≤ −Probable.' },
      { color: OVERVIEW_ZONE_COLORS.strongShort, label: 'Strong Short', when: 'HTF score ≤ −Strong.' },
    ],
    notes: (<>Strength (High / Medium / Low) is a label only — it does not change position size.
      ISL = previous-2 CLH ± buffer; Target = entry ± N × risk.</>),
  };

  const dailyblastOverviewSpec: StrategyOverviewSpec = {
    title: 'DailyBlast — Heikin-Ashi + Linear-Regression Breakout',
    summary: (<>DailyBlast classifies every bar into a 4-state Heikin-Ashi / linear-regression
      regime, waits for a <strong>"prepare"</strong> state, then triggers a breakout entry when
      price clears the prior 2-bar Heikin-Ashi extreme.</>),
    entry: [
      { tag: 'BUY', tagColor: C.bull, label: 'Breakout long', desc: 'After a prepare-long state (light-green following dark-red), the HA high breaks above the prior 2-bar HA high.' },
      { tag: 'SHORT', tagColor: C.bear, label: 'Breakdown short', desc: 'After a prepare-short state (light-red following dark-green), the HA low breaks below the prior 2-bar HA low.' },
    ],
    exit: [
      { tag: 'FLIP', tagColor: C.neutral, label: 'Opposite trigger', desc: 'An opposite breakout closes and reverses the position.' },
      { tag: 'SL', tagColor: C.bear, label: 'Stop breach', desc: 'A close beyond the stop to the opposite side flips the position.' },
    ],
    candleColors: [
      { color: '#00C853', label: 'Dark green', when: 'Strong bullish — linear-regression up and the body is expanding.' },
      { color: '#B9F6CA', label: 'Light green', when: 'Weakening bearish — "prepare for long".' },
      { color: '#D50000', label: 'Dark red', when: 'Strong bearish — linear-regression down and the body is expanding.' },
      { color: '#FF8A80', label: 'Light red', when: 'Weakening bullish — "prepare for short".' },
    ],
    notes: (<>Strength (High / Medium / Low) is graded by the breakout pivot versus the envelope
      band. BUY entry = prior 2-bar HA high, ISL = prior 2-bar low; SHORT entry = prior 2-bar HA
      low, ISL = prior 2-bar high; Target via ATR / R-multiple.</>),
  };

  const shavedDojiOverviewSpec: StrategyOverviewSpec = {
    title: 'Shaved Doji — CLH-Close Stop-and-Reverse',
    summary: (<>A simple, always-in-the-market flip driven purely by the <strong>CLH (Custom
      Linreg-HA)</strong> close. It compares each CLH bar's close to the previous CLH close and
      flips direction when that turns up or down. Computed on CLH closes only, so it behaves
      identically no matter which candle type you display.</>),
    entry: [
      { tag: 'BUY', tagColor: C.bull, label: 'Flip long', desc: 'The CLH close rises above the previous CLH close — go LONG (and cover any short).' },
      { tag: 'SHORT', tagColor: C.bear, label: 'Flip short', desc: 'The CLH close falls below the previous CLH close — go SHORT (and sell any long).' },
    ],
    exit: [
      { tag: 'COVER', tagColor: C.bull, label: 'Cover on Buy', desc: 'A BUY flip closes an open short before opening the long.' },
      { tag: 'SELL', tagColor: C.bear, label: 'Sell on Short', desc: 'A SHORT flip closes an open long before opening the short.' },
    ],
    candleColors: [
      { color: C.bull, label: 'Green', when: 'Position state is LONG (last CLH-close move was up).' },
      { color: C.bear, label: 'Red', when: 'Position state is SHORT (last CLH-close move was down).' },
      { color: C.neutral, label: 'Neutral', when: 'FLAT — before the first flip.' },
    ],
    notes: (<>A signal fires only on the FLIP (the bar where the running direction changes), not
      on every bar. An equal CLH close carries the previous direction. The strategy is always in a
      position once the first flip occurs — there is no separate flat exit. ISL / Target are not
      used; the opposite flip is the exit.</>),
  };

  const blastOverviewSpec: StrategyOverviewSpec = {
    title: 'Blast — Inside / Engulfing → C-POC Breakout',
    summary: (<>Finds compression / expansion candles on the <strong>Daily &amp; Weekly</strong>{' '}
      timeframes and waits for a breakout. An <strong>Inside</strong> candle (smoothed HA: high ≤
      prev high AND low ≥ prev low) or an <strong>Engulfing</strong> candle (Regular: engulfs the
      prior range and closes beyond its body) draws a band at the candle's High/Low that stays
      active for the next 2 periods. While that band is visible, a fresh close-cross of the
      <strong> C-POC</strong> band fires the signal. Detection is independent of the chart's
      candle type / interval.</>),
    entry: [
      { tag: 'BUY', tagColor: C.bull, label: 'Breakout up', desc: 'Inside/Engulfing band active AND a candle freshly closes ABOVE the C-POC top.' },
      { tag: 'SHORT', tagColor: C.bear, label: 'Breakout down', desc: 'Inside/Engulfing band active AND a candle freshly closes BELOW the C-POC bottom.' },
    ],
    exit: [
      { tag: 'COVER', tagColor: C.bull, label: 'Cover on Buy', desc: 'A BUY breakout closes an open short before opening the long.' },
      { tag: 'SELL', tagColor: C.bear, label: 'Sell on Short', desc: 'A SHORT breakout closes an open long before opening the short.' },
    ],
    candleColors: [
      { color: C.bull, label: 'Green', when: 'Position state is LONG (last breakout was up).' },
      { color: C.bear, label: 'Red', when: 'Position state is SHORT (last breakout was down).' },
      { color: C.neutral, label: 'Neutral', when: 'FLAT — before the first breakout.' },
    ],
    notes: (<>The C-POC is the per-period volume-profile band for the scan timeframe's CPR base
      (Daily for intraday, Weekly for hourly+). A signal fires only on a fresh cross while a
      band is active; bands expire 2 periods after detection.</>),
  };

  // Bhairav — production rules with the curated, backtest-validated gate ladder.
  const bhairavOverviewSpec: StrategyOverviewSpec = {
    title: 'Bhairav — body-high/low stop-and-reverse with trend gates (4H / 1D / 1W)',
    summary: (<>Runs on <strong>your selected candle type</strong> (HA or CLH — any other display type
      computes on CLH). With <strong>body-high = max(open, close)</strong> and <strong>body-low =
      min(open, close)</strong> of the transformed candles, each CLOSED bar is compared to the
      previous one. One position at a time, <strong>stop-and-reverse</strong>. Entries then pass a
      <strong> timeframe-specific trend gate</strong>, curated from the multi-round backtest study:
      <strong> 1D</strong> — AvgCloud green/red <em>and</em> close above/below EMA50;
      <strong> 4H</strong> — the <em>Daily AND Weekly</em> Bhairav state must already align with the
      entry side; <strong>1W</strong> — no gate. Timeframes <strong>below 4H are disabled</strong> —
      every tested configuration loses to trading costs. Exits are never gated (a blocked
      reversal simply closes the trade). The Charts <strong>Bhairav</strong> chip shows exactly
      these gated signals (blocked raw entries print as small grey b/s dots), so Bar Replay +
      the bottom-left badge simulate precisely what autotrade would do.</>),
    entry: [
      { tag: 'BUY', tagColor: C.bull, label: 'Fresh body-high breakout + gate', desc: "Candle's body-high closes ABOVE the previous candle's body-high afresh AND the timeframe's trend gate agrees (1D: AvgCloud green + close > EMA50 · 4H: Daily & Weekly Bhairav both LONG · 1W: no gate). Reverses an open short (COVER + BUY)." },
      { tag: 'SHORT', tagColor: C.bear, label: 'Fresh body-low breakdown + gate', desc: "Candle's body-low closes BELOW the previous candle's body-low afresh AND the gate agrees (1D: AvgCloud red + close < EMA50 · 4H: Daily & Weekly Bhairav both SHORT · 1W: no gate). Reverses an open long (SELL + SHORT)." },
    ],
    exit: [
      { tag: 'SELL', tagColor: C.bear, label: 'Long exit (never gated)', desc: "While LONG: the candle's body-high closes AT-OR-BELOW the previous body-high — or a reversal fired whose SHORT side the gate blocked. The dashed trail line shows this level one bar ahead." },
      { tag: 'COVER', tagColor: C.bull, label: 'Short exit (never gated)', desc: "While SHORT: the candle's body-low closes AT-OR-ABOVE the previous body-low — or a reversal fired whose BUY side the gate blocked." },
    ],
    candleColors: [
      { color: C.bull, label: 'Green', when: 'Gated position is LONG.' },
      { color: C.bear, label: 'Red', when: 'Gated position is SHORT.' },
      { color: C.neutral, label: 'Neutral', when: 'FLAT (or timeframe below 4H — strategy inactive).' },
    ],
    notes: (<>Backtest provenance (Backtest Studio, BTC/ETH/PAXG multi-year + NSE indices): 1D gated
      ≈ +2.1%/trade, PF ~1.7; 4H Daily+Weekly-aligned ≈ +0.66%/trade, PF 1.56, max DD 29%,
      OOS-stable; sub-4H negative in every variant — hence the 4H/1D/1W restriction. One global
      condition set is used for ALL symbols: per-symbol tuning was tested and it overfits
      (out-of-sample it lost to the global pick). Gap-fill rule: an engulfing-body bar from flat
      takes the candle's direction. The amber dashed <strong>trail line</strong> is the rule-implied
      exit level for the NEXT bar. The "PROD curated ladder" row in the Backtest page's Bhairav
      ranking simulates exactly these live rules.</>),
  };

  // OM_Simple — the CLN colour-flip rule. Deliberately describes what the chart
  // SHOWS, and states the measured economics rather than implying an edge.
  const omSimpleOverviewSpec: StrategyOverviewSpec = {
    title: 'OM_Simple — CLN colour flip, entries only (any interval, incl. 7H)',
    summary: (<>The CLN candle type colours every bar by its <strong>linear regression</strong>, never
      by the candle's own direction: green when the regression of the CLOSES sits at or above the
      regression of the OPENS, red otherwise. OM_Simple fires on the bar that colour CHANGES —
      <strong> red → green is BUY</strong>, <strong>green → red is SHORT</strong>. That is the whole
      rule. There are <strong>no Sell/Cover signals</strong>: a BUY is closed only by the next SHORT,
      so it is always in the market and carries <strong>no stop of its own</strong> — the stop and
      target are yours to place on the chart after confirming the signal by eye. For that reason the
      engine <strong>refuses auto-trade outright</strong>; no schedule can arm it. Signals are decided
      on <strong>CLOSED bars only</strong> — the forming bar's regression moves on every tick, so a
      marker never appears until the bar is finished. This is a <em>sibling</em> of the Om chart
      strategy, not a version of it, and none of Om's measured record applies here.</>),
    entry: [
      { tag: 'BUY', tagColor: C.bull, label: 'CLN turned red → green', desc: 'On the closed bar where linreg(close, N) crossed to at-or-above linreg(open, N). Reverses an open short. N is Chart Settings → OM_Simple → signal regression length, default 50.' },
      { tag: 'SHORT', tagColor: C.bear, label: 'CLN turned green → red', desc: 'On the closed bar where linreg(close, N) fell below linreg(open, N). Reverses an open long.' },
    ],
    exit: [
      { tag: '—', tagColor: C.neutral, label: 'There are none, by design', desc: 'The position is reversed by the next opposite signal and nothing else. No stop, no target, no time exit. Place them yourself on the chart once you have confirmed the signal.' },
    ],
    // W and C are CHART MARKS, not signals. They appear here because this table is
    // the closest thing the app has to a marker legend, and a reader seeing a
    // circle on the chart needs somewhere to look it up.
    chartOnly: [
      { tag: 'W', tagColor: C.warn, label: 'The leg you are in is weakening', desc: 'Reversal pressure against the open position crossed 45 of 100. Coloured for the direction a flip WOULD go, so while long it is a red W above the bar. Not a trade and not an exit. Measured over 48 symbols on 4H bars, a flip follows within 10 bars 70% of the time after a W, against a 45% base rate.' },
      { tag: 'C', tagColor: C.neutral, label: 'The leg you are in is resuming', desc: 'Trend pressure for the open position crossed 55 of 100 AND the higher timeframe agrees. Coloured for the direction you already hold. A reason to keep holding, never a new entry — OM_Simple is always in the market. Hidden entirely on an interval with no higher timeframe to check.' },
    ],
    candleColors: [
      { color: C.bull, label: 'Green', when: 'Position is LONG (suppressed on a CLN chart, where the regression colour is the point).' },
      { color: C.bear, label: 'Red', when: 'Position is SHORT.' },
      { color: C.neutral, label: 'Neutral', when: 'Before the first signal — the regression is still warming up.' },
    ],
    notes: (<>Measured, not claimed. 10 majors, Mar 2024 – Sep 2026, 0.20% round-trip cost, bars built
      from one hourly base so the timeframes compare directly: at <strong>7H / length 50</strong> the
      rule fired 11.3 times a month per symbol for <strong>+0.045% a trade (PF 1.03, positive on 7 of
      10 symbols)</strong>. At the chart's default length of 14 it fired 22.5 times for −0.125%
      (PF 0.91, 1 of 10). At 8H/50 it was −0.096%. Read that as <strong>roughly break-even at best</strong>
      — a signal to confirm by eye, not an edge to automate, which is exactly how it is wired. This is
      a single-pass measurement, NOT a pre-registered study with a train/test split and ablations of
      the kind behind Bhairav's and Om's numbers; do not compare it to those directly.
      <br /><br />
      <strong>7H and other non-divisor intervals.</strong> 420 does not divide 1440, so no scheduler
      cadence can equal it. The schedule instead ticks on a finer grid that contains every 7H close and
      the engine emits once per closed bar. Note that a 7H day on a 24×7 venue is <strong>7H + 7H + 7H
      + 3H</strong> — the grid restarts at IST midnight, so the last bar of each day is short. That is
      exactly what the chart draws, and the scan reads the identical bars: the rule file is a
      byte-identical twin of the chart's, enforced by <code>scripts/check-engine-parity.mjs</code>.</>),
  };

  // LR_Cross — close crosses the LR body. Describes exactly what the chart and
  // the alert show, and says plainly that nothing about it has been measured.
  const lrCrossOverviewSpec: StrategyOverviewSpec = {
    title: 'LR_Cross — candle closes across the LR body (alerts only)',
    summary: (<>The <strong>LR body</strong> is the linear-regression body the Charts <strong>LR</strong> chip
      draws: its top is the higher of the regression of the opens and of the closes, its bottom the lower,
      built from the real candles at <strong>your chart's LR length</strong> (Chart Settings → Candle; there
      is no separate length here). LR_Cross raises an alert on the <strong>first closed bar</strong> whose
      source close is beyond its <strong>own</strong> body: above the top is <strong>BUY</strong>, below
      the bottom is <strong>SHORT</strong>. The source candle is <strong>Regular or Heikin-Ashi</strong>,
      set above; the Charts marks have their own source under Chart Settings → LR_Cross. A cross the other
      way also <strong>exits</strong> the open side in the same alert (titled <strong>BUY ✕S</strong> /
      <strong> SHORT ✕L</strong>). Alerts go to the Dashboard and Telegram; you act on each one by hand —
      Execute, or set NB/NS/BB/BS levels (optionally with a ⚡ option strategy). The engine
      <strong> refuses auto-trade</strong>.</>),
    entry: [
      { tag: 'BUY', tagColor: C.bull, label: 'First close above the LR body top', desc: "The source close is above the top of this bar's LR body, and the previous bar's close was not above its own. Decided on the closed bar only. Option idea named in the alert when the symbol has a listed chain: Bull Put (credit)." },
      { tag: 'SHORT', tagColor: C.bear, label: 'First close below the LR body bottom', desc: "The mirror: below the bottom of this bar's body, while the previous close was not. Option idea: Bear Call (credit)." },
    ],
    exit: [
      { tag: '✕', tagColor: C.neutral, label: 'The opposite cross', desc: 'An open long is exited by the next SHORT cross and an open short by the next BUY — always the same bar, so it is one alert (SHORT ✕L / BUY ✕S). No stop and no target are set for you.' },
    ],
    chartOnly: [
      { tag: 're', tagColor: C.neutral, label: 'Re-cross the same way', desc: 'Price fell back into the body and crossed out the same way again while already in. Still alerted, labelled re-cross.' },
    ],
    candleColors: [
      { color: C.neutral, label: 'Unchanged', when: 'LR_Cross does not recolour candles; turn on the LR chip to see the body the crosses are measured against.' },
    ],
    notes: (<>Not backtested. At a long LR length (e.g. 50) the body is thin — about a tenth of an average
      bar — so in practice the rule is "the close crosses the regression line", and chop produces
      alternating alerts. Heikin-Ashi closes roughly halve the alerts and usually arrive a bar later.
      Every price in the alert is the real close; the HA close only decides when the cross happened.
      The chart marks and the scheduled scan run the same rule file (byte-identical twins).</>),
  };

  const overviewTabForStrategy = (
    <StrategyOverview spec={
      selectedStrategy === 'om_simple' ? omSimpleOverviewSpec
        : selectedStrategy === 'lr_cross' ? lrCrossOverviewSpec
        : selectedStrategy === 'sniper' ? sniperOverviewSpec
        : selectedStrategy === 'dailyblast' ? dailyblastOverviewSpec
        : selectedStrategy === 'shaved_doji' ? shavedDojiOverviewSpec
        : selectedStrategy === 'blast' ? blastOverviewSpec
        : selectedStrategy === 'bhairav' ? bhairavOverviewSpec
        : rockbandOverviewSpec
    } />
  );

  // ── Tab 6: How to Use ────────────────────────────────────────────────────────

  const rockbandGuideTab = (
    <Grid container spacing={2}>

      {/* Quick-start */}
      <Grid size={{ xs: 12 }}>
        <Card sx={{ borderLeft: `4px solid ${C.neutral}` }}>
          <CardHeader title="Quick-Start — 4 Steps to First Signal" />
          <CardContent>
            {[
              {
                step: '1', title: 'Set thresholds (Settings tab)',
                body: 'Open the Settings tab and review the OverView score thresholds — HTF Buy Min, TF flip levels and exit levels for both long and short. ' +
                  'Defaults (HTF ≥ +20, TF flips through 0, exit at ∓20) suit an aggressive style. Click Save Settings.'
              },
              {
                step: '2', title: 'Build a Watchlist (Watchlist page)',
                body: 'On the Watchlist page, create a named watchlist and add the symbols you want RockBand to scan. Watchlists are shared across all strategies. ' +
                  'Candles are auto-fetched on demand — there is no separate tracked/warmed list to maintain.'
              },
              {
                step: '3', title: 'Adhoc Run (Strategy Scan tab)',
                body: 'Pick the watchlist and a scan timeframe, then click Run Scan. RockBand scores each symbol on the scan TF and its mapped Higher Timeframe. ' +
                  'It logs a BUY/SHORT when the HTF is aligned and the chart-TF score flips through the entry level. Adhoc results stay on this page only.'
              },
              {
                step: '4', title: 'Schedule It (Scheduled Scan panel)',
                body: 'Configure cadence (every N minutes / hours / days-at-HH:MM IST) and enable routing to Dashboard and/or Telegram, then toggle ON. ' +
                  'Each scheduled run also refreshes LTP / MaxGain% / MaxGain time on the Signal Log. Auto-Trade is not wired yet — signals only.'
              }
            ].map(({ step, title, body }) => (
              <Box key={step} sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
                <Box sx={{
                  minWidth: 32, height: 32, borderRadius: '50%',
                  backgroundColor: 'primary.main', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: '0.9rem', flexShrink: 0
                }}>
                  {step}
                </Box>
                <Box>
                  <Typography fontWeight={700}>{title}</Typography>
                  <Typography variant="body2" color="text.secondary">{body}</Typography>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
      </Grid>

      {/* RockBand strategy explained */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardHeader title="What is RockBand?" />
          <CardContent>
            <Typography variant="body2" color="text.secondary" paragraph>
              RockBand (R119) reads two things on each closed bar: the <strong>candle colour</strong> on the scan
              timeframe (the chart-TF OverView zone) and the <strong>background colour</strong> — always the mapped
              Higher Timeframe's OverView zone. A Green candle (score ≥ +60) with a supportive HTF background fires a
              BUY; a Red candle (≤ −60) with a supportive HTF background fires a SHORT. Each entry carries a
              High / Medium / Low strength label. Positions are sized from the per-symbol investment amount on the
              Instruments page (strength does not change size).
            </Typography>
            <Typography variant="subtitle2" gutterBottom>Signals generated:</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              {(['BUY', 'SHORT', 'SELL_FLAT', 'COVER_FLAT'] as const).map((action) => ({
                action,
                desc: action === 'BUY' ? 'Enter long — Green candle (chart-TF ≥ +60) + HTF background Green (High) / Blue trending up (Medium) / Black trending up (Low)' :
                      action === 'SHORT' ? 'Enter short — Red candle (chart-TF ≤ −60) + HTF background Red (High) / Orange trending down (Medium) / Black trending down (Low)' :
                      action === 'SELL_FLAT' ? 'Exit long (SELL) — candle turns Red while long with no qualifying SHORT' :
                      'Exit short (COVER) — candle turns Green while short with no qualifying BUY'
              })).map(({ action, desc }) => (
                <Box key={action} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Chip label={action} size="small" sx={{ bgcolor: `${actionColor(action, C)}22`, color: actionColor(action, C), border: `1px solid ${actionColor(action, C)}55`, fontWeight: 700, minWidth: 90 }} />
                  <Typography variant="body2" color="text.secondary">{desc}</Typography>
                </Box>
              ))}
            </Box>
            <Typography variant="subtitle2" gutterBottom>The OverView score (8 indicators):</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              The score is a weighted blend of EMAs, AvgCloud, CPR, C-POC, H-POC, RelStrength, ChgN% and MACD — each
              voting Up / Down / Neutral. The Higher Timeframe is taken from your Interval → Higher-Timeframe mapping
              (shared with Sniper). Tune the entry/exit boundaries on the Settings tab.
            </Typography>
            <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>On the Charts page:</Typography>
            <Typography variant="body2" color="text.secondary">
              Select <strong>RockBand</strong> from the strategy dropdown (after Sniper). Every candle is recoloured by
              its chart-TF trend score (Strong / Probable Short → Neutral → Probable / Strong Buy), and B / S / X
              markers show the live BUY / SHORT entries and SELL / COVER exits — matching the scan exactly. Turn on
              the Bkground indicator (Overview_HTF method) to see the HTF background the signals use.
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Market hours */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardHeader title="Market Hours (Evaluation Window)" />
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Signals are only evaluated during the active market window for each exchange.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Exchange</TableCell>
                  <TableCell>Window (IST)</TableCell>
                  <TableCell>Days</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  ['DELTA (crypto)', '24 × 7', 'Every day'],
                  ['NSE / NFO / BSE', '09:15 – 15:30', 'Mon – Fri'],
                  ['MCX', '09:00 – 23:30', 'Mon – Fri'],
                  ['CDS / BFO', '09:00 – 17:00', 'Mon – Fri'],
                ].map(([exch, window, days]) => (
                  <TableRow key={exch}>
                    <TableCell>{exch}</TableCell>
                    <TableCell>{window}</TableCell>
                    <TableCell>{days}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              MCX/CDS/BFO symbols warm at <strong>08:55 IST</strong> (5 min before MCX opens).
              NSE/BSE/NFO symbols warm at <strong>09:08 IST</strong> (7 min before NSE opens).
              Delta symbols warm at server start (24×7).
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Position sizing & auto-trade */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardHeader title="Position Sizing & Auto-Trade" />
          <CardContent>
            <Typography variant="body2" color="text.secondary" paragraph>
              Each signal carries a suggested quantity computed from the symbol's <strong>investment amount</strong> on
              the Instruments page (segment / mode aware — equity, futures or options budget), divided by the entry
              price × lot size and adjusted for leverage. No fixed stop-loss or target is set.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
              <Chip label="Note" size="small" sx={{ bgcolor: C.warnMuted, color: C.warn, border: `1px solid ${C.warnBorder}`, fontSize: '0.65rem' }} />
              <Typography variant="body2" color="text.secondary">
                <strong>Auto-Trade is intentionally NOT wired yet</strong> for RockBand — it produces signals only, so you
                can validate quality first. Orders are not placed automatically. Auto-Trade will be enabled in a later
                round once the signal quality is confirmed.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Technical: how the engine decides */}
      <Grid size={{ xs: 12 }}>
        <Card sx={{ borderLeft: `4px solid ${C.accent}` }}>
          <CardHeader title="Technical Overview — How RockBand Decides" />
          <CardContent>
            <Typography variant="body2" color="text.secondary" paragraph>
              The server engine (<code>overviewScan.service.ts</code> + <code>overviewEngine.ts</code>) is a faithful
              port of the Charts OverView trend, so a chart and the scanner agree on every score.
            </Typography>

            <Typography variant="subtitle2" gutterBottom>Step 1 — Fetch candles (on demand)</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              For each watchlist symbol the scanner fetches the scan-TF candles, the mapped HTF candles, and a benchmark
              series for each (BTCUSD/ETHUSD for Delta; NIFTY 50 / NIFTY BANK for NSE). No warm-up step — candles are
              pulled and cached when the scan runs.
            </Typography>

            <Typography variant="subtitle2" gutterBottom>Step 2 — Score both timeframes</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Eight indicators (EMAs, AvgCloud, CPR, C-POC, H-POC, RelStrength, ChgN%, MACD) each vote Up / Down /
              Neutral and are weighted into a −100…+100 score for the scan-TF and the HTF. OverView parameters come from
              your active Chart Settings template, so the strategy score matches the dashboard you evaluate.
            </Typography>

            <Typography variant="subtitle2" gutterBottom>Step 3 — Entry (aggressive, HTF-aligned)</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              <code>htfBull = htfScore ≥ HTF Buy Min</code>; <code>htfBear = htfScore ≤ HTF Short Max</code>.
              <code> buyFlip = prevTF &lt; TF Buy Level AND TF ≥ TF Buy Level</code> (mirror for shortFlip).
              A LONG fires on <code>htfBull AND buyFlip</code>; a SHORT on <code>htfBear AND shortFlip</code> — the
              earliest hint once the HTF agrees.
            </Typography>

            <Typography variant="subtitle2" gutterBottom>Step 4 — Exit (score-based)</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              While LONG: exit (<code>SELL_FLAT</code>) when <code>TF &lt; TF Exit Long</code> OR the HTF stops being
              aligned. While SHORT: exit (<code>COVER_FLAT</code>) when <code>TF &gt; TF Exit Short</code> OR the HTF
              stops being aligned. Per-symbol position state is tracked so the same action is not re-fired or re-routed
              until it changes. No fixed SL / target.
            </Typography>

            <Typography variant="subtitle2" gutterBottom>Step 5 — Forward-performance analytics</Typography>
            <Typography variant="body2" color="text.secondary">
              On each scheduled run the engine refreshes the active signal's <strong>LTP</strong>, <strong>MaxGain%</strong>{' '}
              and <strong>MaxGain time</strong> (and MaxAdverse%) from that scan's last candle High / Low / Close —
              no continuous polling. Tracking freezes when the position exits or after a safety bar cap.
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Troubleshooting */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardHeader title="Troubleshooting" />
          <CardContent>
            {[
              {
                q: 'No signals appearing',
                a: 'RockBand only fires when the HTF is aligned AND the chart-TF score flips through the entry level. Loosen thresholds on the Settings tab (e.g. lower HTF Buy Min), confirm the watchlist has symbols, and run an adhoc scan.'
              },
              {
                q: 'Too many / too few signals',
                a: 'Raise HTF Buy Min (and lower HTF Short Max) for stricter HTF alignment, or widen the TF flip levels. Higher thresholds = fewer, higher-conviction entries.'
              },
              {
                q: 'Scan shows "insufficient warmup"',
                a: 'Some indicators need a long history (EMA 200 + CSH warm-up). Pick a scan timeframe with enough cached bars, or re-run after candles have been fetched once.'
              },
              {
                q: 'Quantity looks wrong / is 1',
                a: 'Set the investment amount for that symbol on the Instruments page (matching its segment & mode). Without it the engine defaults the suggested quantity to 1.'
              },
              {
                q: 'Chart colours/arrows not showing',
                a: 'On the Charts page pick "RockBand" from the strategy dropdown. The score series needs ≥ ~250 bars to warm up; zoom out if the early bars are uncoloured.'
              },
            ].map(({ q, a }) => (
              <Box key={q} sx={{ mb: 1.5 }}>
                <Typography variant="body2" fontWeight={700} color="primary">{q}</Typography>
                <Typography variant="body2" color="text.secondary">{a}</Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      </Grid>

    </Grid>
  );

  const dailyblastGuideTab = (
    <Grid container spacing={2}>

      {/* Quick-start for DailyBlast */}
      <Grid size={{ xs: 12 }}>
        <Card sx={{ borderLeft: `4px solid ${C.neutral}` }}>
          <CardHeader title="Quick-Start — Running DailyBlast" />
          <CardContent>
            {[
              {
                step: '1', title: 'Create a Watchlist',
                body: 'Go to the Watchlist page and create a named watchlist (e.g. "Bank Index"). Watchlists are shared across all your strategies.'
              },
              {
                step: '2', title: 'Add Symbols',
                body: 'On the Watchlist page, use "Add symbol to this watchlist" — the autocomplete shows both symbol and exchange. ' +
                  'DailyBlast scans whatever symbols you put in the watchlist — there is no separate tracked/warmed list.'
              },
              {
                step: '3', title: 'Adhoc Run (Strategy Scan tab)',
                body: 'Pick the watchlist, pick an analytical timeframe (e.g. 15m, 1h, 1D), and click Run Scan. ' +
                  'Candles are auto-fetched on demand for any symbol not yet cached locally — no manual warm-up needed. ' +
                  'Signals appear in the page only; they do NOT fan out to Dashboard alerts or Telegram.'
              },
              {
                step: '4', title: 'Schedule It (Scheduled Scan panel)',
                body: 'Configure cadence: Every N minutes / hours / days-at-HH:MM IST. Enable routing to Dashboard (Recent Alerts) and/or Telegram. ' +
                  'Toggle ON. From then on the scan runs automatically and each new, non-duplicate signal is delivered to the channels you enabled.'
              },
            ].map(({ step, title, body }) => (
              <Box key={step} sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
                <Box sx={{
                  minWidth: 32, height: 32, borderRadius: '50%',
                  backgroundColor: 'primary.main', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: '0.9rem', flexShrink: 0
                }}>
                  {step}
                </Box>
                <Box>
                  <Typography fontWeight={700}>{title}</Typography>
                  <Typography variant="body2" color="text.secondary">{body}</Typography>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
      </Grid>

      {/* Collapsible description sections */}
      <Grid size={{ xs: 12 }}>
        <Accordion defaultExpanded={false} sx={{ '&:before': { display: 'none' }, boxShadow: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={700}>Learn More — What is DailyBlast, Adhoc vs Scheduled, Technical Details, Troubleshooting</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>

      {/* What is DailyBlast */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardHeader title="What is DailyBlast?" />
          <CardContent>
            <Typography variant="body2" color="text.secondary" paragraph>
              DailyBlast is a <strong>state-transition breakout</strong> strategy. It smooths raw OHLC with a
              14-period linear regression, builds Heikin-Ashi candles on top of the smoothed values, and
              classifies every bar into one of four states based on <em>direction</em> (bullish / bearish) and
              <em> body size vs. the previous bar</em> (expanding / contracting).
            </Typography>
            <Typography variant="subtitle2" gutterBottom>The 4 states:</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label="Dark Green" size="small" sx={{ bgcolor: '#00C853', color: '#fff', fontWeight: 700, minWidth: 110 }} />
                <Typography variant="body2" color="text.secondary">Bullish bar, body <strong>expanding</strong> — strong uptrend momentum.</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label="Light Red" size="small" sx={{ bgcolor: '#FF8A80', color: '#000', fontWeight: 700, minWidth: 110 }} />
                <Typography variant="body2" color="text.secondary">Bullish bar, body <strong>contracting</strong> — uptrend slowing (cooling phase).</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label="Dark Red" size="small" sx={{ bgcolor: '#D50000', color: '#fff', fontWeight: 700, minWidth: 110 }} />
                <Typography variant="body2" color="text.secondary">Bearish bar, body <strong>expanding</strong> — strong downtrend momentum.</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label="Light Green" size="small" sx={{ bgcolor: '#B9F6CA', color: '#000', fontWeight: 700, minWidth: 110 }} />
                <Typography variant="body2" color="text.secondary">Bearish bar, body <strong>contracting</strong> — downtrend slowing (cooling phase).</Typography>
              </Box>
            </Box>
            <Typography variant="subtitle2" gutterBottom>Signals:</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Chip label="BUY" size="small" sx={{ bgcolor: `${C.bull}22`, color: C.bull, border: `1px solid ${C.bull}55`, fontWeight: 700, minWidth: 70 }} />
                <Typography variant="body2" color="text.secondary">
                  State flips <strong>Light Green → Dark Green</strong> AND close breaks above the <strong>prior 2-bar high</strong>.
                  Initial stop-loss = prior 2-bar low.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Chip label="SHORT" size="small" sx={{ bgcolor: `${C.bear}22`, color: C.bear, border: `1px solid ${C.bear}55`, fontWeight: 700, minWidth: 70 }} />
                <Typography variant="body2" color="text.secondary">
                  State flips <strong>Light Red → Dark Red</strong> AND close breaks below the <strong>prior 2-bar low</strong>.
                  Initial stop-loss = prior 2-bar high.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Adhoc vs Scheduled */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardHeader title="Adhoc vs. Scheduled Scan" />
          <CardContent>
            <Typography variant="body2" color="text.secondary" paragraph>
              Strategy Scan (DailyBlast and any other scan strategy) distinguishes two run modes:
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>Adhoc (Run Scan)</TableCell>
                  <TableCell>Scheduled</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  ['Trigger', 'Manual button click', 'Every N min / hour / day @ HH:MM IST'],
                  ['Results shown in page', 'Yes', 'Yes (last run)'],
                  ['Dashboard Recent Alerts', 'No', 'Optional (switch)'],
                  ['Telegram notification', 'No', 'Optional (switch)'],
                  ['Duplicate suppression', 'No', 'Yes — dedup by (symbol, action, candle time)'],
                  ['Candle auto-fetch', 'Yes', 'Yes'],
                ].map(([row, adhoc, sched]) => (
                  <TableRow key={row}>
                    <TableCell sx={{ fontWeight: 700 }}>{row}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{adhoc}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{sched}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Use Adhoc when you're exploring — noise from repeated clicks does not pollute Dashboard or Telegram.
              Switch to Scheduled once you've settled on a watchlist + timeframe and want signals auto-delivered.
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Auto data pull */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardHeader title="Auto Data Pull (no manual warm-up)" />
          <CardContent>
            <Typography variant="body2" color="text.secondary" paragraph>
              When a scan runs, for each symbol in the watchlist the backend:
            </Typography>
            <Box component="ol" sx={{ pl: 2, mb: 1 }}>
              <Box component="li" sx={{ mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">Resolves the instrument row (auto-creates a stub for Delta crypto if missing).</Typography>
              </Box>
              <Box component="li" sx={{ mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">Fetches / backfills the required candles via the shared candle cache (Zerodha for NSE/BSE/NFO, Delta for crypto).</Typography>
              </Box>
              <Box component="li" sx={{ mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">Evaluates DailyBlast on the latest closed candle.</Typography>
              </Box>
              <Box component="li" sx={{ mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">If a new signal fires and this is a <em>scheduled</em> run with routing enabled, creates the Alert row and/or sends Telegram.</Typography>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Symbols with too little history (new listings, stale instruments) are marked
              <Chip label="SKIPPED_INSUFFICIENT_DATA" size="small" sx={{ ml: 0.5, fontSize: '0.65rem', height: 18 }} />
              in the per-symbol progress list — the scan continues without crashing.
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Technical */}
      <Grid size={{ xs: 12 }}>
        <Card sx={{ borderLeft: `4px solid ${C.accent}` }}>
          <CardHeader title="Technical Overview — How DailyBlast Computes Each Bar" />
          <CardContent>
            <Typography variant="body2" color="text.secondary" paragraph>
              Reference implementation: <code>backend/src/services/dailyBlast.service.ts</code>. Identical logic
              runs on the frontend chart overlay so the visible candles match the scanner's decisions bar-for-bar.
            </Typography>

            <Typography variant="subtitle2" gutterBottom>Step 1 — Smooth raw OHLC</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Apply a <strong>14-period linear regression</strong> independently to Open, High, Low, and Close.
              Result: <code>lrO, lrH, lrL, lrC</code> — the LR-projected value at the most recent bar for each series.
              This replaces the noisy raw candle with a smoother trend-aligned one.
            </Typography>

            <Typography variant="subtitle2" gutterBottom>Step 2 — Heikin-Ashi on the smoothed values</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              <code>c = (lrO + lrH + lrL + lrC) / 4</code><br />
              <code>o = (prev_o + prev_c) / 2</code> (seeded with <code>(lrO + lrC) / 2</code> on the first bar)<br />
              <code>h = max(lrH, o, c)</code><br />
              <code>l = min(lrL, o, c)</code>
            </Typography>

            <Typography variant="subtitle2" gutterBottom>Step 3 — Classify state</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              <code>body = |c − o|</code>. <code>isBullish = c ≥ o</code>.{' '}
              <code>expanding = body &gt; previousBody</code>.
            </Typography>
            <Box sx={{ fontFamily: 'monospace', fontSize: '0.78rem', backgroundColor: 'action.hover', p: 1.5, borderRadius: 1, mb: 1 }}>
              isBullish &amp;&amp;  expanding → dark_green<br />
              isBullish &amp;&amp; !expanding → light_red<br />
              !isBullish &amp;&amp;  expanding → dark_red<br />
              !isBullish &amp;&amp; !expanding → light_green
            </Box>

            <Typography variant="subtitle2" gutterBottom>Step 4 — Fire entry signals</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              <strong>BUY</strong>: prevState = <code>light_green</code>, state = <code>dark_green</code>, AND
              <code> close &gt; max(high[−1], high[−2])</code>. ISL = <code>min(low[−1], low[−2])</code>.
              <br /><strong>SHORT</strong>: prevState = <code>light_red</code>, state = <code>dark_red</code>, AND
              <code> close &lt; min(low[−1], low[−2])</code>. ISL = <code>max(high[−1], high[−2])</code>.
              <br />Both signals are emitted with <code>strength = HIGH</code>.
            </Typography>

            <Typography variant="subtitle2" gutterBottom>Constants</Typography>
            <Box sx={{ fontFamily: 'monospace', fontSize: '0.78rem', backgroundColor: 'action.hover', p: 1.5, borderRadius: 1 }}>
              LR_LEN = 14 &nbsp; HA_SEED_OPEN = (lrO + lrC) / 2 &nbsp; MIN_BARS = LR_LEN + 3
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Troubleshooting */}
      <Grid size={{ xs: 12 }}>
        <Card>
          <CardHeader title="Troubleshooting" />
          <CardContent>
            {[
              {
                q: '"Strategy Not Found" when running a scan',
                a: 'Your account has no strategy selected. The backend auto-creates a default "General Scan" on first use — refresh the page.'
              },
              {
                q: 'Scan finishes with 0 signals',
                a: 'DailyBlast only fires on the specific state transition. In calm markets most bars are CLEAN (no signal) — this is expected.'
              },
              {
                q: 'A symbol shows SKIPPED_INSUFFICIENT_DATA',
                a: 'That symbol has fewer than ~17 bars on the chosen timeframe. Try a shorter interval (e.g. 5m) or a longer-lived symbol.'
              },
              {
                q: 'Scheduled run produced no Dashboard alerts',
                a: 'Confirm the Scheduled Scan panel has "Route to Dashboard" ON and is enabled. Adhoc scans never route — only scheduled runs do.'
              },
              {
                q: 'Same signal appears twice after re-scheduling',
                a: 'Duplicate suppression is per-(symbol, action, candle time). If you scan a different interval or delete + re-create the schedule, the dedup key changes.'
              },
            ].map(({ q, a }) => (
              <Box key={q} sx={{ mb: 1.5 }}>
                <Typography variant="body2" fontWeight={700} color="primary">{q}</Typography>
                <Typography variant="body2" color="text.secondary">{a}</Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      </Grid>

            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>

    </Grid>
  );

  // ── Sniper strategy tabs (Watchlist-based, dual-timeframe) ──────────────────
  const sniperGuideTab = (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardHeader title={<Typography variant="h6" fontWeight={700}>How Sniper Works — the mechanics</Typography>} />
        <CardContent>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.5 }}>The cloud</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Sniper reads the same <strong>AvgCloud</strong> you draw on the Charts page, with the cloud's
            reference line set to <strong>CMid</strong> (Combined Midline — the 3-timeframe blend of
            LTF + CTF + HTF Bollinger bands). The cloud is the shaded region between the
            <strong> LR_HAMA_Band</strong> edge and <strong>CMid</strong>. It is
            <strong> green</strong> when the LR_HAMA average sits above CMid and <strong>red</strong> when
            it sits below. Enable AvgCloud + CMid + LR_HAMA_Band on any chart to see exactly what Sniper
            scans.
          </Typography>
          <Typography variant="subtitle2" fontWeight={700}>Entry events — cloud-colour flip</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
            <Typography variant="body2"><Chip label="BUY" size="small" sx={{ bgcolor: C.bull, color: C.onSolid, fontWeight: 700, mr: 1 }} />Latest closed bar is <strong>green</strong> AND the most recent prior non-neutral bar was <strong>red</strong> — cloud just flipped bullish.</Typography>
            <Typography variant="body2"><Chip label="SHORT" size="small" sx={{ bgcolor: C.bear, color: C.onSolid, fontWeight: 700, mr: 1 }} />Latest closed bar is <strong>red</strong> AND the most recent prior non-neutral bar was <strong>green</strong> — cloud just flipped bearish.</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Signals fire on the <strong>first flip bar</strong>. The "most recent prior non-neutral" rule
            tolerates the occasional NEUTRAL bar (exact LR_HAMA centre = CMid) without missing a real
            regime change. The per-symbol state-dedup then prevents re-firing the same action until the
            cloud flips again.
          </Typography>
          <Typography variant="subtitle2" fontWeight={700}>Trend (Higher Timeframe)</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Trend is the cloud colour: <strong style={{ color: C.bull }}>green → LONG</strong>,
            <strong style={{ color: C.bear }}> red → SHORT</strong>. Sniper evaluates the cloud on the
            <strong> Higher Timeframe</strong> mapped to your scan interval (Timeframe Mapping panel at
            the top of this page). If the HTF cloud can't be computed (insufficient CTF/HTF history for
            CMid to converge), the trend is indeterminate and the signal is <strong>suppressed</strong>.
          </Typography>
          <Typography variant="subtitle2" fontWeight={700}>Dual-timeframe confirmation</Typography>
          <Typography variant="body2" color="text.secondary">
            A scan-timeframe <strong>BUY</strong> is only confirmed when the Higher-Timeframe cloud is
            currently <strong>green</strong>; a <strong>SHORT</strong> only when the HTF cloud is
            <strong> red</strong>. Everything else is dropped. The scan-interval trend is recorded with
            each signal — in the Signal Log the Symbol cell is tinted green for a Long-trend signal and
            red for a Short-trend signal.
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardHeader title={<Typography variant="h6" fontWeight={700}>Running it live — step by step</Typography>} />
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>1. Set your timeframe mappings.</strong> Open the <strong>Timeframe Mapping</strong>{' '}
            panel at the top of the Strategies page and confirm each scan interval points at the Higher
            Timeframe you want for trend confirmation (e.g. 15m → 1H). Defaults are pre-filled; adjust and Save.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>2. Build a Watchlist.</strong> On the <strong>Watchlist</strong> page, create a
            watchlist and add the symbols you want Sniper to scan. Watchlists are shared across strategies.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>3. Adhoc scan (test/one-off).</strong> On the <strong>Strategy Scan</strong> tab pick
            the watchlist + scan interval and click <strong>Run Scan</strong>. Sniper evaluates every
            symbol on the scan interval, then re-checks each flip candidate on its Higher Timeframe.
            Adhoc results are shown on this page only — <em>no Dashboard Alert and no Telegram</em>.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>4. Scheduled scan (live run).</strong> On the same tab, configure the
            <strong> Scheduled Scan</strong> panel: watchlist, interval, cadence, and the
            <strong> Route to Dashboard</strong> / <strong>Route to Telegram</strong> toggles. The
            scheduler then runs Sniper automatically on that cadence. Confirmed signals are routed to the
            channels you enabled — Dashboard "Recent Alerts" and/or Telegram — exactly like DailyBlast and
            RockBand. The same action on the same symbol/interval won't re-alert until the cloud flips again.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>5. Review in the Signal Log.</strong> Every confirmed signal — adhoc or scheduled —
            is recorded in the <strong>Signal Log</strong> tab with its scan interval, Higher Timeframe,
            trend, prior cloud colour, and entry price. Use the day / symbol / action filters to narrow
            it down. Click the <strong>Chart</strong> button on any row to open that symbol's chart with
            the AvgCloud + CMid + LR_HAMA_Band indicators so you can eyeball the setup.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>6. Act on it.</strong> When Route to Dashboard is on, confirmed signals land in the
            Dashboard's Recent Alerts where you can execute them into Trade Management. Sniper is a signal
            generator — it does not place orders by itself.
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title={<Typography variant="h6" fontWeight={700}>Notes &amp; limits</Typography>} />
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
            • Sniper evaluates the <strong>latest closed candle</strong> — it does not act on an unfinished
            bar — so a confirmed signal won't repaint.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
            • CMid is built from a 3-timeframe Bollinger blend (LTF + CTF + HTF). The scan fetches ~1000
            CTF and HTF bars per symbol so EMA(200) at CTF / HTF cadence converges — fresh listings or
            short histories may produce indeterminate signals until enough data is available.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Higher Timeframe data is fetched per the Timeframe Mapping table. If a mapping is missing,
            Sniper falls back to a sensible default for that interval.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );

  // ── Signal Log — CurrentGain + tri-state column sorting ─────────────────────
  // CurrentGain = gain if the trade were OPENED at the signal's entry and STILL
  // held at the latest price (LTP). Per the spec: Cover signals assume a Buy was
  // taken (long), Sell signals assume a short was taken — so BUY/COVER_FLAT are
  // long, SHORT/SELL_FLAT are short.
  const currentGainOf = (sig: StrategySignalItem): number | null => {
    const entry = sig.entryPrice != null ? Number(sig.entryPrice) : null;
    const ltp = sig.currentPrice != null ? Number(sig.currentPrice) : null;
    if (entry == null || ltp == null || !Number.isFinite(entry) || !Number.isFinite(ltp) || entry === 0) return null;
    const longSide = sig.action === 'BUY' || sig.action === 'COVER_FLAT';
    return longSide ? ((ltp - entry) / entry) * 100 : ((entry - ltp) / entry) * 100;
  };

  const signalSortValue = (sig: StrategySignalItem, col: string): number | string | null => {
    const cd: any = sig.computationData || {};
    switch (col) {
      case 'time': return new Date(sig.candleTime || sig.createdAt || 0).getTime();
      case 'symbol': return (sig.symbol || '').toUpperCase();
      case 'signal': return sig.action || '';
      case 'trend': return cd.scanTf?.trend || (sig.action === 'BUY' ? 'LONG' : 'SHORT');
      case 'scanTf': return sig.intervalMinutes ?? null;
      case 'htf': return cd.htf?.interval ?? null;
      case 'entry': return sig.entryPrice != null ? Number(sig.entryPrice) : null;
      case 'ltp': return sig.currentPrice != null ? Number(sig.currentPrice) : null;
      case 'currentGain': return currentGainOf(sig);
      case 'maxGain': return sig.maxGainPct != null ? Number(sig.maxGainPct) : null;
      case 'maxGainAt': return sig.maxGainAt ? new Date(sig.maxGainAt).getTime() : null;
      case 'maxAdv': return sig.maxAdversePct != null ? Number(sig.maxAdversePct) : null;
      default: return null;
    }
  };

  const sortedScanSignals = useMemo(() => {
    if (!signalSortCol || !signalSortDir) return scanSignals;
    const dir = signalSortDir === 'asc' ? 1 : -1;
    const arr = [...scanSignals];
    arr.sort((a, b) => {
      const va = signalSortValue(a, signalSortCol);
      const vb = signalSortValue(b, signalSortCol);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;   // nulls always sink to the bottom
      if (vb == null) return -1;
      if (typeof va === 'string' || typeof vb === 'string') return String(va).localeCompare(String(vb)) * dir;
      return ((va as number) - (vb as number)) * dir;
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanSignals, signalSortCol, signalSortDir]);

  // Tri-state header click: asc → desc → off (original order).
  const onSignalSort = (col: string) => {
    if (signalSortCol !== col) { setSignalSortCol(col); setSignalSortDir('asc'); return; }
    if (signalSortDir === 'asc') { setSignalSortDir('desc'); return; }
    if (signalSortDir === 'desc') { setSignalSortCol(null); setSignalSortDir(null); return; }
    setSignalSortDir('asc');
  };
  const signalSortArrow = (col: string) => {
    const active = signalSortCol === col && !!signalSortDir;
    const glyph = active ? (signalSortDir === 'asc' ? '▲' : '▼') : '↕';
    return <Box component="span" sx={{ ml: 0.3, fontSize: '0.7em', color: active ? 'text.primary' : 'text.disabled', opacity: active ? 1 : 0.5 }}>{glyph}</Box>;
  };
  const sortTh = (col: string, label: React.ReactNode, opts?: { title?: string; sx?: any }) => (
    <TableCell title={opts?.title} onClick={() => onSignalSort(col)}
      sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...(opts?.sx || {}) }}>
      {label}{signalSortArrow(col)}
    </TableCell>
  );

  // ── Sniper Signal Log — confirmed dual-timeframe signals ────────────────────
  const sniperSignalLogTab = (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight="bold">Signal Log</Typography>
          <Typography variant="caption" color="text.secondary">
            {selectedStrategy === 'sniper'
              ? 'Confirmed Sniper signals — scan-timeframe entry aligned with the Higher-Timeframe trend. '
              : selectedStrategy === 'rockband'
                ? 'RockBand (OverView) signals — aggressive entries when the HTF is aligned and the chart-TF score flips through the threshold. '
                : selectedStrategy === 'shaved_doji'
                  ? 'Shaved Doji signals — BUY / SHORT flips on CLH-close direction. '
                  : selectedStrategy === 'blast'
                    ? 'Blast signals — BUY / SHORT on a fresh C-POC breakout while an Inside/Engulfing band is active. '
                    : 'DailyBlast signals. '}
            The Symbol cell is tinted by direction (green = Long/Buy, red = Short).
            LTP / MaxGain% / MaxGain time track forward performance, refreshed each scan from the last candle.
          </Typography>
        </Box>
        <Button
          variant="contained" size="small" color="primary"
          startIcon={<RefreshIcon />}
          onClick={refreshSignalsPerf}
          disabled={refreshingPerf || scanSignals.length === 0}
          title="Recompute LTP / MaxGain% / MaxGain time / MaxAdv% for the visible signals"
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          {refreshingPerf ? 'Refreshing…' : 'Refresh LTP & MaxGain/MaxAdv'}
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="caption" fontWeight={700} sx={{ mr: 1 }}>
          Last (calendar days, IST):
        </Typography>
        {[1, 2, 3, 5, 7, 14, 30].map(d => (
          <Chip key={d} label={`${d}d`} size="small" clickable
            // Setting a chip clears any pinned specific date so the filter
            // intent is unambiguous.
            onClick={() => { setSignalCandleDate(''); setSignalDayFilter(d); }}
            disabled={!!signalCandleDate}
            sx={{ fontWeight: signalDayFilter === d && !signalCandleDate ? 700 : 400,
              bgcolor: signalDayFilter === d && !signalCandleDate ? C.neutralMuted : undefined,
              border: signalDayFilter === d && !signalCandleDate ? `1px solid ${C.neutral}` : undefined,
              opacity: signalCandleDate ? 0.5 : 1 }} />
        ))}
        <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>or pin date:</Typography>
        <TextField size="small" type="date" sx={{ width: 150 }}
          inputProps={{ style: { padding: '4px 8px', fontSize: '0.8rem' } }}
          value={signalCandleDate}
          onChange={e => setSignalCandleDate(e.target.value)}
          title="Show signals whose bar-time falls inside this single IST day" />
        {signalCandleDate && (
          <Chip label="Clear date" size="small" onClick={() => setSignalCandleDate('')}
            sx={{ ml: 0.5 }} />
        )}
        <TextField size="small" placeholder="Symbol" sx={{ ml: 1, width: 120 }}
          inputProps={{ style: { padding: '4px 8px', fontSize: '0.8rem' } }}
          value={scanSignalFilterSymbol} onChange={e => setScanSignalFilterSymbol(e.target.value.toUpperCase())} />
        {/* F-Strategies-ActionChips — multi-select chips. Empty selection = show every action. */}
        <Typography variant="caption" sx={{ ml: 1, fontWeight: 700 }}>Action:</Typography>
        {(['BUY', 'SHORT', 'SELL_FLAT', 'COVER_FLAT'] as const).map(act => {
          const on = scanSignalFilterActions.includes(act);
          const col = actionColor(act, C);
          return (
            <Chip key={act} label={act} size="small" clickable
              onClick={() => setScanSignalFilterActions(prev =>
                prev.includes(act) ? prev.filter(a => a !== act) : [...prev, act])}
              sx={{ fontWeight: 700, fontSize: '0.7rem',
                bgcolor: on ? col : 'transparent',
                color: on ? C.onSolid : col,
                border: `1px solid ${col}` }} />
          );
        })}
      </Box>
      {/* F-Strategies-ActionChips — full-width Alert so the user can't miss
          that the table view is filtered. Click "Clear" to remove the filter. */}
      {scanSignalFilterActions.length > 0 && (
        <Alert
          severity="info"
          variant="outlined"
          icon={false}
          sx={{
            mb: 1.5, fontSize: '0.85rem', py: 0.4, alignItems: 'center',
            // Soft amber tint instead of the harsh blue fill — eye-friendly
            // while still grabbing attention.
            bgcolor: '#fff8e1',
            color: '#5d4037',
            borderColor: '#ffb74d',
          }}
          action={
            <Button color="inherit" size="small" onClick={() => setScanSignalFilterActions([])}
              sx={{ fontWeight: 700, color: '#5d4037' }}>Clear filter</Button>
          }
        >
          Showing <strong>{scanSignalFilterActions.join(' and ')}</strong> signals only — other actions are hidden.
        </Alert>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            {sortTh('time', 'Time')}
            {sortTh('symbol', 'Symbol')}
            {sortTh('signal', 'Signal')}
            {sortTh('trend', 'Trend')}
            {sortTh('scanTf', <>Scan TF <span style={{ color: '#888', fontWeight: 400 }}>ⓘ</span></>,
              { title: 'Timeframe the scan ran on. Number in brackets is the OverView trend score at the signal bar (−100 = strong bearish, 0 = neutral, +100 = strong bullish).' })}
            {sortTh('htf', 'HTF')}
            {sortTh('entry', 'Entry')}
            {sortTh('ltp', 'LTP', { title: 'Latest price (last candle close)' })}
            {sortTh('currentGain', 'CurrentGain',
              { title: 'Gain if the trade were opened at Entry and still held at LTP. Cover signals assume a Buy was taken (long); Sell signals assume a short was taken.' })}
            {sortTh('maxGain', 'MaxGain%', { title: 'Best % gain reached after the signal' })}
            {sortTh('maxGainAt', 'MaxGain @', { title: 'Time the max gain was reached' })}
            {sortTh('maxAdv', 'MaxAdv%', { title: 'Worst % drawdown after the signal' })}
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedScanSignals.map(sig => {
            const cd = sig.computationData || {};
            const scanTrend: string = cd.scanTf?.trend || (sig.action === 'BUY' ? 'LONG' : 'SHORT');
            const htfInterval: number | undefined = cd.htf?.interval;
            const htfTrend: string | undefined = cd.htf?.trend;
            const tfScore: number | undefined = cd.tf?.score;            // RockBand
            const htfScore: number | undefined = cd.htf?.score;          // RockBand
            const isLong = scanTrend === 'LONG';
            const symBg = isLong ? C.bullMuted : C.bearMuted;
            const symFg = isLong ? C.bull : C.bear;
            const mg = sig.maxGainPct != null ? Number(sig.maxGainPct) : null;
            const ma = sig.maxAdversePct != null ? Number(sig.maxAdversePct) : null;
            const cg = currentGainOf(sig);
            return (
              <TableRow key={sig.id} hover>
                {/* Show candleTime (= the actual bar this signal fired on)
                    so it matches the chart's tooltip. createdAt is the scan
                    run-time and is misleading for adhoc/manual scans run
                    days after the signal bar. */}
                <TableCell sx={{ fontSize: '0.75rem' }} title={`Scanned at ${formatTs(sig.createdAt)}`}>
                  {sig.candleTime ? formatTs(sig.candleTime) : formatTs(sig.createdAt)}
                </TableCell>
                <TableCell sx={{ bgcolor: symBg, borderLeft: `3px solid ${symFg}` }}>
                  <strong style={{ color: symFg }}>{sig.symbol}</strong>{' '}
                  <Typography component="span" variant="caption" color="text.secondary">{sig.exchange}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={sig.action} size="small" sx={{ fontWeight: 700, fontSize: '0.7rem', color: C.onSolid, bgcolor: actionColor(sig.action, C) }} />
                </TableCell>
                <TableCell>
                  <Chip label={scanTrend} size="small" variant="outlined"
                    sx={{ fontWeight: 700, fontSize: '0.65rem', color: symFg, borderColor: symFg }} />
                </TableCell>
                <TableCell sx={{ fontSize: '0.78rem' }}
                  title={tfScore != null
                    ? `OverView score at the signal bar: ${tfScore > 0 ? '+' : ''}${tfScore}. Frozen at signal time — doesn't change on refresh.`
                    : undefined}>
                  {labelFor(sig.intervalMinutes)}
                  {tfScore != null && (
                    <Typography component="span" variant="caption" sx={{ ml: 0.5, color: tfScore >= 0 ? C.bull : C.bear }}>
                      ({tfScore > 0 ? '+' : ''}{tfScore})
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: '0.78rem' }}
                  title={htfInterval
                    ? `Higher-timeframe (${minutesLabel(htfInterval)}) cloud direction at the signal bar — frozen at signal time.`
                    : 'HTF info not recorded for this signal.'}>
                  {htfInterval ? minutesLabel(htfInterval) : '—'}
                  {htfTrend && (
                    <Typography component="span" variant="caption" sx={{ ml: 0.5, color: htfTrend === 'LONG' ? C.bull : htfTrend === 'SHORT' ? C.bear : C.dim }}>
                      ({htfTrend})
                    </Typography>
                  )}
                  {htfTrend == null && htfScore != null && (
                    <Typography component="span" variant="caption" sx={{ ml: 0.5, color: htfScore >= 0 ? C.bull : C.bear }}>
                      ({htfScore > 0 ? '+' : ''}{htfScore})
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: '0.78rem' }}>{sig.entryPrice != null ? Number(sig.entryPrice).toFixed(4) : '—'}</TableCell>
                <TableCell sx={{ fontSize: '0.78rem' }}>{sig.currentPrice != null ? Number(sig.currentPrice).toFixed(4) : '—'}</TableCell>
                <TableCell sx={{ fontSize: '0.78rem', fontWeight: 700, color: cg == null ? 'text.secondary' : cg > 0 ? C.bull : cg < 0 ? C.bear : C.dim }}
                  title="Gain if opened at Entry and still held at LTP">
                  {cg == null ? '—' : `${cg > 0 ? '+' : ''}${cg.toFixed(2)}%`}
                </TableCell>
                <TableCell sx={{ fontSize: '0.78rem', fontWeight: 700, color: mg == null ? 'text.secondary' : mg > 0 ? C.bull : C.dim }}>
                  {mg == null ? '—' : `${mg > 0 ? '+' : ''}${mg.toFixed(2)}%`}
                </TableCell>
                <TableCell sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{sig.maxGainAt ? formatTs(sig.maxGainAt) : '—'}</TableCell>
                <TableCell sx={{ fontSize: '0.78rem', fontWeight: 700, color: ma == null ? 'text.secondary' : ma < 0 ? C.bear : C.dim }}>
                  {ma == null ? '—' : `${ma.toFixed(2)}%`}
                </TableCell>
                <TableCell>
                  <Chip
                    icon={<CandlestickChartIcon sx={{ fontSize: 16 }} />}
                    label="Chart"
                    size="small"
                    clickable
                    variant="outlined"
                    onClick={() => {
                      const extra: Record<string, string> = {};
                      if (sig.intervalMinutes) extra.interval = String(sig.intervalMinutes);
                      if ((sig as any).strategyId || scanStrategyId) extra.strategy = String((sig as any).strategyId || scanStrategyId);
                      openChartTab(sig.symbol, sig.exchange || '', extra);
                    }}
                    sx={{ fontSize: '0.7rem', height: 22 }}
                  />
                </TableCell>
              </TableRow>
            );
          })}
          {scanSignals.length === 0 && (
            <TableRow>
              <TableCell colSpan={13} align="center" sx={{ py: 1, borderBottom: 'none' }}>
                <EmptyState kind="inbox" size="sm" title="No signals yet"
                  description="Run a scan from the Strategy Scan tab — matching BUY / SHORT / SELL / COVER signals appear here." />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {/* F-Strategies-NoPagination — date filter already caps the result
          set; show a small count line instead of paginating. */}
      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
        Showing {scanSignals.length} of {scanSignalTotal} signals
        {scanSignalTotal !== scanSignals.length && ' (narrow the date range to load more)'}
      </Typography>
    </Box>
  );

  const shavedDojiGuideTab = (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <Card sx={{ borderLeft: `4px solid ${C.neutral}` }}>
          <CardHeader title="How Shaved Doji works" />
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Shaved Doji is a pure <strong>CLH-close stop-and-reverse</strong>. Every closed CLH
              (Custom Linreg-HA) bar is compared to the previous one:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }}>
              <Typography variant="body2"><Chip label="BUY" size="small" sx={{ bgcolor: C.bull, color: C.onSolid, fontWeight: 700, mr: 1 }} />CLH close &gt; previous CLH close → go LONG (covering any open short).</Typography>
              <Typography variant="body2"><Chip label="SHORT" size="small" sx={{ bgcolor: C.bear, color: C.onSolid, fontWeight: 700, mr: 1 }} />CLH close &lt; previous CLH close → go SHORT (selling any open long).</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              The position flips on each direction change — a Buy covers a short, a Short sells a
              long; it is always in the market after the first flip. Candles are coloured by the
              running state (green LONG / red SHORT). The logic reads CLH closes only, so it is
              identical across all display candle types. A signal fires only on the flip, not on
              every bar; an equal close carries the previous direction.
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              <strong>Getting started:</strong> 1) Create a Watchlist and add symbols (Watchlist page).
              2) Run an Adhoc scan or configure a Scheduled Scan from the Strategy Scan tab.
              Confirmed flips appear in the Signal Log; scheduled scans can fan out to Dashboard / Telegram.
              To see it on a chart, open Charts and pick <strong>Shaved Doji</strong> as the strategy.
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const blastGuideTab = (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <Card sx={{ borderLeft: `4px solid ${C.neutral}` }}>
          <CardHeader title="How Blast works" />
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Blast hunts for the calm-before-the-storm on the <strong>Daily &amp; Weekly</strong>{' '}
              timeframes, then trades the breakout:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }}>
              <Typography variant="body2"><Chip label="Inside" size="small" sx={{ bgcolor: C.neutral, color: C.onSolid, fontWeight: 700, mr: 1 }} />Smoothed HA candle: high ≤ prev high AND low ≥ prev low (compression).</Typography>
              <Typography variant="body2"><Chip label="Engulfing" size="small" sx={{ bgcolor: C.neutral, color: C.onSolid, fontWeight: 700, mr: 1 }} />Regular candle that engulfs the prior range and closes beyond its body (expansion).</Typography>
              <Typography variant="body2"><Chip label="BUY" size="small" sx={{ bgcolor: C.bull, color: C.onSolid, fontWeight: 700, mr: 1 }} />While a band is active, a candle freshly closes ABOVE the C-POC top.</Typography>
              <Typography variant="body2"><Chip label="SHORT" size="small" sx={{ bgcolor: C.bear, color: C.onSolid, fontWeight: 700, mr: 1 }} />While a band is active, a candle freshly closes BELOW the C-POC bottom.</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Each detected Daily/Weekly candle draws a band at its High/Low that stays active for
              the next 2 periods. Detection is independent of the chart's candle type / interval.
              The position flips on each fresh breakout — a Buy covers a short, a Short sells a long.
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              <strong>Getting started:</strong> 1) Create a Watchlist and add symbols (Watchlist page).
              2) Run an Adhoc scan or configure a Scheduled Scan from the Strategy Scan tab.
              Confirmed breakouts appear in the Signal Log; scheduled scans can fan out to Dashboard / Telegram.
              To see the bands on a chart, open Charts and toggle the <strong>Blast</strong> indicator.
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const lrCrossGuideTab = (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <Card sx={{ borderLeft: `4px solid ${C.neutral}` }}>
          <CardHeader title="Using LR_Cross" />
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              <strong>1)</strong> Pick the source candle on the Overview tab. <strong>2)</strong> On Charts, select the
              <strong> LR_Cross</strong> strategy chip, turn on the <strong>LR</strong> chip and set Chart Settings → LR_Cross to
              the same source, so you see each crossing close against its body. <strong>3)</strong> In Strategy Scan, add a
              scheduled scan for your watchlist and timeframe with Dashboard / Telegram routing. <strong>4)</strong> When an
              alert arrives (e.g. <em>BTCUSD: BUY ✕S</em>), review it on the chart and act yourself: Execute, or set an
              NB/NS/BB/BS level, optionally with a ⚡ option strategy (the alert names Bull Put for BUY, Bear Call for SHORT
              when the symbol has options). Nothing is placed automatically.
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const guideTab = selectedStrategy === 'lr_cross' ? lrCrossGuideTab : selectedStrategy === 'dailyblast'
    ? dailyblastGuideTab
    : selectedStrategy === 'sniper'
      ? sniperGuideTab
      : selectedStrategy === 'shaved_doji'
        ? shavedDojiGuideTab
        : selectedStrategy === 'blast'
          ? blastGuideTab
          : rockbandGuideTab;

  // ── Strategy cards ────────────────────────────────────────────────────────────
  // STRATEGIES now lives at module scope (top of file) — it is static data, and
  // the schedule-chip effect needs it to know which rows are actually rendered.

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* When embedded in the Strategy hub the tab label already says "Strategies",
          so suppress the redundant page title — keep just the loading spinner. */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: embedded ? 0 : 2 }}>
        {!embedded && <Typography variant="h5" fontWeight={700}>Strategies</Typography>}
        {loading && <CircularProgress size={24} sx={{ ml: 'auto' }} />}
      </Box>

      {/* Phase B — per-engine signal-volume strip */}
      <StrategyComparisonStrip />

      {/* Shared infra: Interval → Higher-Timeframe mapping (used by Sniper) */}
      <TimeframeMappingPanel />

      {/* Strategy selector */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {STRATEGIES.map(s => (
          <Grid key={s.id} size={{ xs: 12, sm: 4 }}>
            <Card
              onClick={() => { if (s.status === 'active') { setSelectedStrategy(s.id); setTab(0); } }}
              sx={{
                cursor: s.status === 'active' ? 'pointer' : 'default',
                border: selectedStrategy === s.id ? '2px solid' : '2px solid transparent',
                borderColor: selectedStrategy === s.id ? 'primary.main' : 'transparent',
                opacity: s.status === 'coming_soon' ? 0.65 : 1,
                transition: 'border-color 0.15s',
                '&:hover': s.status === 'active' ? { borderColor: 'primary.light' } : {}
              }}
            >
              <CardContent sx={{ pb: '12px !important' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography fontWeight={700}>{s.name}</Typography>
                  {(() => {
                    if (s.status !== 'active') {
                      return (
                        <Chip
                          label="COMING SOON" size="small"
                          sx={{
                            bgcolor: C.dimMuted, color: C.dim,
                            border: `1px solid ${C.dim}44`,
                            fontWeight: 700, fontSize: '0.65rem'
                          }}
                        />
                      );
                    }
                    // Reflect scheduled-scan state for every card. ACTIVE means a
                    // schedule exists AND is enabled (i.e. the in-house scanner is
                    // actively producing alerts on that cadence). PAUSED means a
                    // schedule exists but is currently turned off via the Pause
                    // button. INACTIVE means no schedule has been configured yet —
                    // previously this case was painted as ACTIVE which is misleading.
                    const isSelected = selectedStrategy === s.id;
                    // Tab-independent: the chip reflects the page-level
                    // scheduleEnabledMap (fetched for EVERY card on mount, no
                    // matter which sub-tab is open). Pause/Resume on the selected
                    // strategy mirrors into that map, so toggles still update
                    // instantly. We only fall back to the live `scheduleEnabled`
                    // when the map hasn't loaded yet — this stops the not-yet-
                    // loaded live value (null until the Strategy Scan tab opens)
                    // from mis-showing the card as INACTIVE.
                    const mapVal = scheduleEnabledMap[normStrategyKey(s.id)];
                    const effective = mapVal !== undefined ? mapVal : (isSelected ? scheduleEnabled : null);
                    let label: 'ACTIVE' | 'PAUSED' | 'INACTIVE';
                    if (effective === true) label = 'ACTIVE';
                    else if (effective === false) label = 'PAUSED';
                    else label = 'INACTIVE';
                    const tip = label === 'ACTIVE'
                      ? 'Scheduled Scan is enabled — the scanner is producing alerts on its cadence.'
                      : label === 'PAUSED'
                        ? 'Scheduled Scan is configured but paused. Open the strategy and click Resume on the Scheduled Scan panel.'
                        : 'No Scheduled Scan configured yet. Open the strategy and create one on the Scheduled Scan panel.';
                    const bg = label === 'ACTIVE' ? C.bullMuted : label === 'PAUSED' ? C.warnMuted : C.dimMuted;
                    const fg = label === 'ACTIVE' ? C.bull : label === 'PAUSED' ? C.warn : C.dim;
                    const bd = label === 'ACTIVE' ? C.bullBorder : label === 'PAUSED' ? C.warnBorder : C.dim;
                    return (
                      <Tooltip title={tip} arrow>
                        <Chip
                          label={label}
                          size="small"
                          sx={{
                            bgcolor: bg, color: fg,
                            border: `1px solid ${bd}44`,
                            fontWeight: 700, fontSize: '0.65rem'
                          }}
                        />
                      </Tooltip>
                    );
                  })()}
                </Box>
                <Typography variant="caption" color="text.secondary">{s.description}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* DailyBlast info card — collapsible description */}
      {selectedStrategy === 'dailyblast' && (
        <Accordion defaultExpanded={false} sx={{ mb: 2, '&:before': { display: 'none' }, boxShadow: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" fontWeight={700}>DailyBlast Strategy — How it works</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              DailyBlast uses a <strong>14-period Linear Regression</strong> to smooth OHLC data, then builds <strong>Heikin-Ashi candles</strong> from the smoothed values.
              Each bar is classified into one of <strong>4 states</strong> based on whether the body is expanding or contracting:
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
              <Chip label="Dark Green — Strong Bullish" size="small" sx={{ bgcolor: '#00C853', color: '#fff', fontWeight: 600 }} />
              <Chip label="Light Red — Weakening Bullish" size="small" sx={{ bgcolor: '#FF8A80', color: '#000', fontWeight: 600 }} />
              <Chip label="Dark Red — Strong Bearish" size="small" sx={{ bgcolor: '#D50000', color: '#fff', fontWeight: 600 }} />
              <Chip label="Light Green — Weakening Bearish" size="small" sx={{ bgcolor: '#B9F6CA', color: '#000', fontWeight: 600 }} />
            </Box>
            <Typography variant="body2" color="text.secondary">
              <strong>Entry signals:</strong> A LONG fires when the state transitions from Light Green → Dark Green AND price breaks above the 2-bar high.
              A SHORT fires on Light Red → Dark Red AND price breaks below the 2-bar low.
              View signals in the Charts page by selecting "DailyBlast" from the strategy dropdown — candles will be colored by state with arrow markers at signal points.
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Operational tabs. Watchlist MANAGEMENT lives on the Watchlist page —
          scans only SELECT a watchlist here (tab indices 0/2/4/6 kept stable
          so the shared TabPanels below work unchanged). */}
      {/* A card whose backing Strategy row is missing must not fall through to
          the scan controls: they would run against whatever strategy id was
          selected last, i.e. a different engine entirely. */}
      {selectedStrategy === 'om_simple' && !userStrategies.some(s => s.engine === 'om_simple') && (
        <Alert severity="info" sx={{ mb: 2 }}>
          OM_Simple has not been created on this account yet — it is added automatically the first
          time the strategy list loads after the backend is updated. The chart overlay
          (Charts → Strategies → OM_Simple) works already; scanning and scheduling appear here once
          the row exists. Reload this page after the next backend deploy.
        </Alert>
      )}

      {selectedStrategy === 'lr_cross' && !userStrategies.some(s => s.engine === 'lr_cross') && (
        <Alert severity="info" sx={{ mb: 2 }}>
          LR_Cross has not been created on this account yet — it is added automatically the first time the
          strategy list loads after the backend is updated. Reload this page after the next backend deploy.
        </Alert>
      )}

      {(selectedStrategy === 'rockband' || selectedStrategy === 'dailyblast' || selectedStrategy === 'sniper' || selectedStrategy === 'shaved_doji' || selectedStrategy === 'blast' || (selectedStrategy === 'om_simple' && userStrategies.some(s => s.engine === 'om_simple')) || (selectedStrategy === 'lr_cross' && userStrategies.some(s => s.engine === 'lr_cross')) || selectedStrategy === 'bhairav') && (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              {selectedStrategy === 'sniper'
                ? [
                    <Tab key="o" label="Overview" value={0} />,
                    <Tab key="sl" label="Signal Log" value={2} />,
                    <Tab key="s" label="Strategy Scan" value={4} />,
                    <Tab key="h" label="How to Use" value={6} />,
                  ]
                : selectedStrategy === 'rockband'
                ? [
                    <Tab key="o" label="Overview" value={0} />,
                    <Tab key="sl" label="Signal Log" value={2} />,
                    <Tab key="ss" label="Strategy Scan" value={4} />,
                    <Tab key="h" label="How to Use" value={6} />,
                  ]
                : [
                    // DailyBlast / others — Symbol Manager + Settings tabs removed
                    // along with the retired in-house polling engine. Watchlist
                    // MANAGEMENT lives on the Watchlist page now (scans only
                    // SELECT a watchlist here).
                    <Tab key="o" label="Overview" value={0} />,
                    <Tab key="sl" label="Signal Log" value={2} />,
                    <Tab key="ss" label="Strategy Scan" value={4} />,
                    <Tab key="h" label="How to Use" value={6} />,
                  ]}
            </Tabs>
          </Box>

          <TabPanel value={tab} index={0}>
            {/* R145 — closed Higher-Timeframe candles toggle (no repaint). */}
            <Card variant="outlined" sx={{ mb: 2, maxWidth: 880 }}>
              <CardContent sx={{ py: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControlLabel
                    sx={{ mr: 0 }}
                    control={strategyEngineKey
                      ? <Switch checked={closedHtfFlag} disabled={closedHtfSaving} onChange={e => handleToggleClosedHtf(e.target.checked)} />
                      : <Switch checked disabled />}
                    label={<Typography variant="body2" fontWeight={700}>Use closed Higher-Timeframe candles only (no repaint)</Typography>}
                  />
                  {closedHtfSaving && <CircularProgress size={16} />}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {strategyEngineKey
                    ? 'When ON, the Higher-Timeframe trend is read only from CLOSED HTF candles, so the latest signal and candle colouring do not repaint as the current HTF bar forms. Applies to scans and the chart overlay (reload the chart after changing). When OFF, the still-forming HTF bar is used — signals can change until it closes.'
                    : 'This is a single-timeframe strategy — it already computes on closed candles only, with no Higher Timeframe to look ahead.'}
                </Typography>
              </CardContent>
            </Card>
            {selectedStrategy === 'lr_cross' && lrCrossRow && (
              <Card variant="outlined" sx={{ mb: 2, maxWidth: 880 }} data-testid="lrcross-source-card">
                <CardContent sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 240 }}>
                      <InputLabel>Source candle (scans and alerts)</InputLabel>
                      <Select label="Source candle (scans and alerts)" value={lrCrossSource} disabled={lrCrossSaving}
                        onChange={e => handleLrCrossSource(e.target.value as 'Regular' | 'HA')}>
                        <MenuItem value="Regular">Regular — real closes</MenuItem>
                        <MenuItem value="HA">Heikin-Ashi closes</MenuItem>
                      </Select>
                    </FormControl>
                    {lrCrossSaving && <CircularProgress size={16} />}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    Applies to adhoc and scheduled scans from the next run. The LR length is your chart's (Chart Settings →
                    Candle). The Charts marks use their own source under Chart Settings → LR_Cross.
                  </Typography>
                </CardContent>
              </Card>
            )}
            {overviewTabForStrategy}
          </TabPanel>
          {/* Scan-based Signal Log (reads strategy_signals incl. forward-performance
              analytics) for all three engines — Sniper, RockBand, DailyBlast. */}
          <TabPanel value={tab} index={2}>{sniperSignalLogTab}</TabPanel>

          {/* Watchlists tab removed — management (create / delete / add / remove
              symbols) is centralized on the Watchlist page; scans only SELECT
              a watchlist in Strategy Scan / Scheduled Scan. */}

          {/* Strategy Scan Tab */}
          <TabPanel value={tab} index={4}>
            {/* Scheduled Scan (auto-runs, routes to Dashboard/Telegram) */}
            {scanStrategyId && (
              <ScheduledScanPanel
                autoTradeRefusedReason={scanEngine === 'om_simple'
                  ? 'OM_Simple never places orders. It is always in the market with no stop of its own — a BUY is closed only by the next SHORT — so the engine refuses auto-trade outright and ignores this switch. Place stop and target by hand after confirming the signal.'
                  : scanEngine === 'lr_cross'
                  ? 'LR_Cross only raises alerts. It never places orders and ignores this switch — act on each alert yourself (Execute, or set NB/NS/BB/BS levels).'
                  : undefined}
                cadenceFor={(m: number, exchanges: string[]) => {
                  const row = customByMinutes.get(m);
                  if (!row) return undefined;
                  const byVenue = row.scanCadenceByVenue;
                  if (!byVenue || !exchanges.length) return row.scanCadence;
                  // Take the finest cadence among the venues this watchlist
                  // actually holds, not among every venue that exists — a
                  // Delta-only 7H schedule should not inherit NSE's answer.
                  const period = (c: { scheduleType: string; scheduleValue: number }) =>
                    c.scheduleType === 'DAY' ? 1440 : c.scheduleType === 'HOUR' ? c.scheduleValue * 60 : c.scheduleValue;
                  const picked = exchanges
                    .map(e => byVenue[e] || byVenue[e === 'DELTA' ? 'DELTA' : 'NSE'])
                    .filter(Boolean);
                  return picked.length
                    ? picked.reduce((a, b) => (period(b) < period(a) ? b : a))
                    : row.scanCadence;
                }}
                strategyId={scanStrategyId}
                watchlists={watchlists as any}
                intervalOptions={scanIntervalOptions}
                minutesLabel={labelFor}
                // F-Strategies-MultiSchedule — stable callback (above).
                onChange={handleScheduleChange}
              />
            )}

            <Card sx={{ mb: 2 }}>
              <CardHeader
                title={<Typography variant="h6" fontWeight={700}>Adhoc Scan</Typography>}
                subheader="Runs once, right now. Results show on this page only — no Dashboard Alert and no Telegram message."
              />
              <CardContent>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Strategy</InputLabel>
                    <Select
                      label="Strategy"
                      value={scanStrategyId || ''}
                      onChange={e => setScanStrategyId(Number(e.target.value) || null)}
                    >
                      {userStrategies.map(s => (
                        <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                      ))}
                      {userStrategies.length === 0 && (
                        <MenuItem value="" disabled>No strategies yet</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Watchlist</InputLabel>
                    <Select label="Watchlist" value={selectedWatchlistId || ''} onChange={e => setSelectedWatchlistId(Number(e.target.value) || null)}>
                      {watchlists.map(wl => <MenuItem key={wl.id} value={wl.id}>{wl.name} ({wl.symbols.length})</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Interval</InputLabel>
                    <Select label="Interval" value={scanIntervalOptions.includes(scanInterval) ? scanInterval : scanIntervalOptions[0]} onChange={e => setScanInterval(Number(e.target.value))}>
                      {scanIntervalOptions.map(m => <MenuItem key={m} value={m}>{labelFor(m)}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField size="small" type="date" label="As-of date (optional)" InputLabelProps={{ shrink: true }}
                    value={scanAsOfDate} onChange={e => setScanAsOfDate(e.target.value)}
                    helperText={scanAsOfDate ? 'Signals on this date only' : 'Live (current bar)'}
                    sx={{ minWidth: 170 }} />
                  {scanAsOfDate && (
                    <Button size="small" variant="text" onClick={() => setScanAsOfDate('')} sx={{ minWidth: 0, px: 1 }}>Clear</Button>
                  )}
                  <Button variant="contained" startIcon={scanRunning ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                    onClick={handleStartScan} disabled={scanRunning || !selectedWatchlistId || !scanStrategyId}
                    sx={{ bgcolor: C.bull }}>
                    {scanRunning ? 'Scanning...' : (scanAsOfDate ? `Run Scan (${scanAsOfDate})` : 'Run Scan')}
                  </Button>
                </Box>

                {scanStrategyId && activeScanJobId && (
                  <ScanProgress
                    key={activeScanJobId}
                    strategyId={scanStrategyId}
                    jobId={activeScanJobId}
                    onDone={handleScanDone}
                  />
                )}

                {lastScanSummary && !scanRunning && (
                  <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Last scan: {lastScanSummary.total} signal{lastScanSummary.total === 1 ? '' : 's'}
                    </Typography>
                    {lastScanSummary.counts.BUY > 0 && (
                      <Chip size="small" label={`BUY: ${lastScanSummary.counts.BUY}`} sx={{ bgcolor: C.bull, color: C.onSolid, fontWeight: 600 }} />
                    )}
                    {lastScanSummary.counts.SHORT > 0 && (
                      <Chip size="small" label={`SHORT: ${lastScanSummary.counts.SHORT}`} sx={{ bgcolor: C.bear, color: C.onSolid, fontWeight: 600 }} />
                    )}
                    {lastScanSummary.counts.SELL > 0 && (
                      <Chip size="small" label={`SELL: ${lastScanSummary.counts.SELL}`} sx={{ bgcolor: C.warn, color: C.onSolid, fontWeight: 600 }} />
                    )}
                    {lastScanSummary.counts.COVER > 0 && (
                      <Chip size="small" label={`COVER: ${lastScanSummary.counts.COVER}`} sx={{ bgcolor: C.neutral, color: C.onSolid, fontWeight: 600 }} />
                    )}
                    {lastScanSummary.total > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Bias: {(lastScanSummary.counts.BUY || 0) > (lastScanSummary.counts.SHORT || 0) ? 'BULLISH' : ((lastScanSummary.counts.SHORT || 0) > (lastScanSummary.counts.BUY || 0) ? 'BEARISH' : 'MIXED')}
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* F-Strategies-RefreshPerf — prominent Refresh + signal filters.
                Days chips and the pin-date filter are mutually exclusive;
                picking one clears the other. */}
            <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained" size="small" color="primary"
                startIcon={<RefreshIcon />}
                onClick={refreshSignalsPerf}
                disabled={refreshingPerf || scanSignals.length === 0}
                title="Recompute LTP / MaxGain% / MaxGain time / MaxAdv% for the visible signals — uses one round-trip, no per-row polling"
                sx={{ textTransform: 'none', fontWeight: 700, mr: 1 }}
              >
                {refreshingPerf ? 'Refreshing…' : 'Refresh LTP & MaxGain/MaxAdv'}
              </Button>
              <Typography variant="caption" fontWeight={700} sx={{ mr: 1 }}>
                Last (calendar days, IST):
              </Typography>
              {[1, 2, 3, 5, 7, 14, 30].map(d => (
                <Chip key={d} label={`${d}d`} size="small" clickable
                  onClick={() => { setSignalCandleDate(''); setSignalDayFilter(d); }}
                  disabled={!!signalCandleDate}
                  sx={{ fontWeight: signalDayFilter === d && !signalCandleDate ? 700 : 400,
                    bgcolor: signalDayFilter === d && !signalCandleDate ? C.neutralMuted : undefined,
                    border: signalDayFilter === d && !signalCandleDate ? `1px solid ${C.neutral}` : undefined,
                    opacity: signalCandleDate ? 0.5 : 1 }} />
              ))}
              <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>or pin date:</Typography>
              <TextField size="small" type="date" sx={{ width: 150 }}
                inputProps={{ style: { padding: '4px 8px', fontSize: '0.8rem' } }}
                value={signalCandleDate}
                onChange={e => setSignalCandleDate(e.target.value)}
                title="Show signals whose bar-time falls inside this single IST day" />
              {signalCandleDate && (
                <Chip label="Clear date" size="small" onClick={() => setSignalCandleDate('')}
                  sx={{ ml: 0.5 }} />
              )}
              <TextField size="small" placeholder="Symbol" sx={{ ml: 1, width: 120 }}
                inputProps={{ style: { padding: '4px 8px', fontSize: '0.8rem' } }}
                value={scanSignalFilterSymbol} onChange={e => setScanSignalFilterSymbol(e.target.value.toUpperCase())} />
              <Typography variant="caption" sx={{ ml: 1, fontWeight: 700 }}>Action:</Typography>
              {(['BUY', 'SHORT', 'SELL_FLAT', 'COVER_FLAT'] as const).map(act => {
                const on = scanSignalFilterActions.includes(act);
                const col = actionColor(act, C);
                return (
                  <Chip key={act} label={act} size="small" clickable
                    onClick={() => setScanSignalFilterActions(prev =>
                      prev.includes(act) ? prev.filter(a => a !== act) : [...prev, act])}
                    sx={{ fontWeight: 700, fontSize: '0.7rem',
                      bgcolor: on ? col : 'transparent',
                      color: on ? C.onSolid : col,
                      border: `1px solid ${col}` }} />
                );
              })}
              <Select size="small" value={scanSignalFilterInterval === '' ? '' : String(scanSignalFilterInterval)}
                onChange={e => {
                  const v = String(e.target.value ?? '');
                  setScanSignalFilterInterval(v === '' ? '' : Number(v));
                }}
                displayEmpty sx={{ minWidth: 110, fontSize: '0.8rem' }}>
                <MenuItem value="">All intervals</MenuItem>
                {signalFilterIntervals.map(m => <MenuItem key={m} value={String(m)}>{labelFor(m)}</MenuItem>)}
              </Select>
              <Button
                size="small" variant="outlined" color="error" startIcon={<DeleteIcon />}
                onClick={async () => {
                  if (!scanStrategyId) return;
                  const scope = scanSignalFilterInterval
                    ? ` for interval ${labelFor(Number(scanSignalFilterInterval))}`
                    : '';
                  if (!window.confirm(`Purge ALL signals${scope}? This cannot be undone.`)) return;
                  try {
                    // Purge keeps using single-action filter for now (multi-action
                    // purge is rare + dangerous; use the chips to narrow the view,
                    // pick ONE action, then purge).
                    const result = await strategyScanService.purgeSignals(scanStrategyId, {
                      intervalMinutes: scanSignalFilterInterval ? Number(scanSignalFilterInterval) : undefined,
                      action: scanSignalFilterActions.length === 1 ? scanSignalFilterActions[0] : undefined,
                      symbol: scanSignalFilterSymbol || undefined,
                    });
                    toast.success(`Purged ${result?.deleted ?? 0} signals`);
                    loadScanSignals();
                  } catch {
                    toast.error('Failed to purge signals');
                  }
                }}
                sx={{ ml: 1 }}
              >
                Purge
              </Button>
            </Box>
            {/* F-Strategies-ActionChips — full-width Alert. */}
            {scanSignalFilterActions.length > 0 && (
              <Alert
                severity="info"
                variant="filled"
                sx={{ mb: 1.5, fontWeight: 700, fontSize: '0.9rem', py: 0.4, alignItems: 'center' }}
                action={
                  <Button color="inherit" size="small" onClick={() => setScanSignalFilterActions([])}
                    sx={{ fontWeight: 700 }}>Clear filter</Button>
                }
              >
                Showing <strong>{scanSignalFilterActions.join(' and ')}</strong> signals only — other actions are hidden.
              </Alert>
            )}

            {/* Scan Signal Results Table */}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Symbol</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Interval</TableCell>
                  <TableCell>Entry</TableCell>
                  <TableCell>Current</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scanSignals.map(sig => (
                  <TableRow key={sig.id} hover>
                    {/* candleTime — actual bar this signal fired on, matches chart. */}
                    <TableCell sx={{ fontSize: '0.75rem' }} title={`Scanned at ${formatTs(sig.createdAt)}`}>
                      {sig.candleTime ? formatTs(sig.candleTime) : formatTs(sig.createdAt)}
                    </TableCell>
                    <TableCell>
                      <strong>{sig.symbol}</strong>{' '}
                      <Typography component="span" variant="caption" color="text.secondary">{sig.exchange}</Typography>
                      {/* Same rule as the watchlist: press to spend, never on render. */}
                      <Box component="span" sx={{ ml: 0.6 }}>
                        <AiVerdictChip
                          symbol={sig.symbol} exchange={sig.exchange}
                          signalDirection={String(sig.action || '').toUpperCase().includes('SELL')
                            || String(sig.action || '').toUpperCase().includes('SHORT') ? 'SHORT' : 'LONG'}
                          askSource="strategy scan results"
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={sig.action} size="small" sx={{ fontWeight: 700, fontSize: '0.7rem', color: C.onSolid, bgcolor: actionColor(sig.action, C) }} />
                    </TableCell>
                    <TableCell>{labelFor(sig.intervalMinutes)}</TableCell>
                    <TableCell>{sig.entryPrice != null ? Number(sig.entryPrice).toFixed(2) : '—'}</TableCell>
                    <TableCell>{sig.currentPrice != null ? Number(sig.currentPrice).toFixed(2) : '—'}</TableCell>
                    <TableCell>
                      <Chip
                        icon={<CandlestickChartIcon sx={{ fontSize: 16 }} />}
                        label="Chart"
                        size="small"
                        clickable
                        variant="outlined"
                        onClick={() => {
                          const extra: Record<string, string> = { candle: 'HA' };
                          if (sig.intervalMinutes) extra.interval = String(sig.intervalMinutes);
                          if ((sig as any).strategyId || scanStrategyId) extra.strategy = String((sig as any).strategyId || scanStrategyId);
                          openChartTab(sig.symbol, sig.exchange || '', extra);
                        }}
                        sx={{ fontSize: '0.7rem', height: 22 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {scanSignals.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      No scan signals found. Run a scan to generate signals.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {/* F-Strategies-NoPagination — single-page view (limit raised to
                2000 server-side, narrow the date range to load more). */}
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
              Showing {scanSignals.length} of {scanSignalTotal} signals
              {scanSignalTotal !== scanSignals.length && ' (narrow the date range to load more)'}
            </Typography>
          </TabPanel>
          <TabPanel value={tab} index={6}>{guideTab}</TabPanel>
        </>
      )}

      {/* Coming-soon placeholder.
          It used to be gated on `!== rockband && !== dailyblast && !== sniper`,
          a list frozen when those were the only three shipped engines. Every
          engine added since — Shaved Doji, Blast, Bhairav and now OM_Simple —
          rendered "under development. Rules will be configured and deployed in a
          future update." directly underneath its own working scan controls.
          The cards already carry the answer in `status`, so ask that instead of
          maintaining a second list that nobody remembers to update. */}
      {STRATEGIES.find(s => s.id === selectedStrategy)?.status === 'coming_soon' && (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {STRATEGIES.find(s => s.id === selectedStrategy)?.name} — Coming Soon
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This strategy is under development. Rules will be configured and deployed in a future update.
            </Typography>
          </CardContent>
        </Card>
      )}

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StrategiesPage;
